
import React, { useState, useEffect, useRef } from 'react';
import { Contract, Deal, Owner, Property, Proposal, PipelineType, Agency, DealStage, UserProfile, Note } from '../types';
import { FileText, Send, CheckCircle, Eye, Download, ArrowLeft, Plus, Search, Home, User, Euro, Calendar, PenTool, Check, Printer, Loader, MapPin, ChevronDown, Building, Lock, Ruler, BedDouble, Users, X, Key, CheckCircle2, RotateCw, Briefcase, Mail, Clock, Phone } from 'lucide-react';
import { storage } from '../services/storage';
import { yousignService } from '../services/yousignService';
import { useModal } from './ui/ModalProvider';

interface ContractBuilderProps {
    dealId?: string | null;
    selectedAgencyId?: string | null;
    onBack?: () => void;
    onNavigateToManaged?: (propertyId: string) => void;
    userProfile?: UserProfile | null;
    onDealUpdated?: () => void; // Callback pour rafraîchir les données dans App.tsx
    yousignEnabled?: boolean; // Feature flag for Yousign
}

export const ContractBuilder: React.FC<ContractBuilderProps> = ({ dealId, selectedAgencyId, onBack, onNavigateToManaged, userProfile, onDealUpdated, yousignEnabled = false }) => {
  const { toast, confirm } = useModal();
  const [viewMode, setViewMode] = useState<'list' | 'create' | 'preview'>('list');
  const [loading, setLoading] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  
  // Yousign State
  const [isSendingYousign, setIsSendingYousign] = useState(false);
  const [isYousignModalOpen, setIsYousignModalOpen] = useState(false);
  
  // Signers State (Input)
  const [clientSigner, setClientSigner] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [agencySigner, setAgencySigner] = useState({ firstName: '', lastName: '', email: '', phone: '' });

  // Yousign Tracking State (Output)
  const [isRefreshingYousign, setIsRefreshingYousign] = useState(false);
  const [signersStatus, setSignersStatus] = useState<any[]>([]); // Detailed signers info from API

  // Data
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);

  // Selection & Forms
  const [selectedDealId, setSelectedDealId] = useState<string>(dealId || '');
  const [selectedProposalId, setSelectedProposalId] = useState<string>(''); 

  const [formData, setFormData] = useState<Partial<Contract>>({
      type: PipelineType.CONCIERGE,
      startDate: new Date().toISOString().split('T')[0],
      duration: '3', // Default 3 months numeric
      commissionRate: 25,
      status: 'DRAFT',
      signedCity: '',
      signedDate: new Date().toISOString().split('T')[0]
  });

  // Owner Override State (If address/civility missing)
  const [ownerDetails, setOwnerDetails] = useState({
      civility: 'Monsieur',
      address: ''
  });

  // Address Autocomplete State
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);

  // Search Proposal State
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
          if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
              setIsDropdownOpen(false);
          }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fonction pour recharger les données
  const reloadData = async () => {
      setLoading(true);
      const [c, d, o, p, prop, a] = await Promise.all([
          storage.getContracts(),
          storage.getDeals(selectedAgencyId),
          storage.getOwners(selectedAgencyId),
          storage.getProperties(selectedAgencyId),
          storage.getProposals(),
          storage.getAllAgencies()
      ]);
      
      let filteredContracts = c;
      if (selectedAgencyId) {
          const visibleDealIds = new Set(d.map(dl => dl.id));
          filteredContracts = c.filter(cnt => visibleDealIds.has(cnt.dealId));
      }

      setContracts(filteredContracts);
      setDeals(d);
      setOwners(o);
      setProperties(p);
      setProposals(prop);
      setAgencies(a);
      setLoading(false);
  };

  // Load Data au chargement initial
  useEffect(() => {
      const fetchData = async () => {
          setLoading(true);
          const [c, d, o, p, prop, a] = await Promise.all([
              storage.getContracts(),
              storage.getDeals(selectedAgencyId),
              storage.getOwners(selectedAgencyId),
              storage.getProperties(selectedAgencyId),
              storage.getProposals(),
              storage.getAllAgencies()
          ]);
          
          let filteredContracts = c;
          if (selectedAgencyId) {
              const visibleDealIds = new Set(d.map(dl => dl.id));
              filteredContracts = c.filter(cnt => visibleDealIds.has(cnt.dealId));
          }

          setContracts(filteredContracts);
          setDeals(d);
          setOwners(o);
          setProperties(p);
          setProposals(prop);
          setAgencies(a);
          setLoading(false);

          if (dealId) {
              // Try to find existing contract
              const existing = filteredContracts.find(cnt => cnt.dealId === dealId);
              if (existing) {
                  const deal = d.find(x => x.id === dealId);
                  const property = deal ? p.find(x => x.id === deal.propertyId) : null;
                  
                  // Clean duration string if it contains "mois" to keep only number
                  let cleanDuration = existing.duration || '3';
                  cleanDuration = cleanDuration.replace(/[^0-9]/g, '');

                  setFormData({
                      ...existing,
                      duration: cleanDuration,
                      signedCity: existing.signedCity || property?.city || '',
                      signedDate: existing.signedDate || new Date().toISOString().split('T')[0]
                  });
                  setSelectedDealId(dealId);
                  if(existing.proposalId) setSelectedProposalId(existing.proposalId);
                  
                  // Load persisted signers status if available
                  // DEFENSIVE PARSING
                  let contentObj = existing.contentJson;
                  if (typeof contentObj === 'string') {
                      try { contentObj = JSON.parse(contentObj); } catch {}
                  }
                  
                  // Load signers from new unified structure
                  if(contentObj?.signers && Array.isArray(contentObj.signers)) {
                      setSignersStatus(contentObj.signers);
                      
                      // Restore signers inputs for editing
                      const agencySignerData = contentObj.signers.find((s: any) => s.role === 'agency');
                      const clientSignerData = contentObj.signers.find((s: any) => s.role === 'client');
                      
                      if (agencySignerData?.info) {
                          setAgencySigner({
                              firstName: agencySignerData.info.first_name || '',
                              lastName: agencySignerData.info.last_name || '',
                              email: agencySignerData.info.email || '',
                              phone: agencySignerData.info.phone_number || ''
                          });
                      }
                      if (clientSignerData?.info) {
                          setClientSigner({
                              firstName: clientSignerData.info.first_name || '',
                              lastName: clientSignerData.info.last_name || '',
                              email: clientSignerData.info.email || '',
                              phone: clientSignerData.info.phone_number || ''
                          });
                      }
                  } else if (contentObj?.signersStatus && contentObj?.submittedSigners) {
                      // Backward compatibility: migrate old structure to new
                      const migratedSigners = contentObj.signersStatus.map((signer: any, index: number) => {
                          const submittedData = index === 0 ? contentObj.submittedSigners.agency : contentObj.submittedSigners.client;
                          return {
                              id: signer.id,
                              role: index === 0 ? 'agency' : 'client',
                              status: signer.status,
                              signed_at: signer.signed_at || signer.signedAt || signer.signature_date,
                              info: {
                                  first_name: submittedData?.firstName || signer.info?.first_name || '',
                                  last_name: submittedData?.lastName || signer.info?.last_name || '',
                                  email: submittedData?.email || signer.info?.email || '',
                                  phone_number: submittedData?.phone || signer.info?.phone_number || ''
                              }
                          };
                      });
                      setSignersStatus(migratedSigners);
                  }

                  setViewMode('preview');
                  
                  // Auto-refresh Yousign status only if pending/ongoing to avoid API spam
                  if (existing.yousignStatus === 'pending' || existing.yousignStatus === 'ongoing') {
                      setTimeout(() => handleRefreshYousignStatus(existing), 1000);
                  }
              } else {
                  // Fallback: If navigating from a Deal without contract, try to find an accepted proposal
                  const linkedProposal = prop.find((pr: Proposal) => pr.dealId === dealId && (pr.status === 'ACCEPTED' || pr.status === 'SENT'));
                  if (linkedProposal) {
                      initCreationFromProposal(linkedProposal.id, d, p, prop, o);
                  }
              }
          }
      };
      fetchData();
  }, [dealId, selectedAgencyId]);

  // Recharger les données quand on revient sur la vue liste
  useEffect(() => {
      if (viewMode === 'list') {
          reloadData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Sync owner details when deal changes
  useEffect(() => {
      if (selectedDealId && owners.length > 0) {
          const deal = deals.find(d => d.id === selectedDealId);
          const owner = owners.find(o => o.id === deal?.ownerId);
          if (owner) {
              setOwnerDetails({
                  civility: owner.civility || 'Monsieur',
                  address: owner.address || ''
              });
          }
      }
  }, [selectedDealId, owners, deals]);

  // Address Search Handler
  const handleAddressSearch = async (query: string) => {
      setOwnerDetails(prev => ({ ...prev, address: query }));
      if (query.length > 3) {
          try {
              const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5`);
              const data = await res.json();
              setAddressSuggestions(data.features || []);
          } catch (e) {
              console.error("Address API Error", e);
          }
      } else {
          setAddressSuggestions([]);
      }
  };

  const selectAddress = (feature: any) => {
      const label = feature.properties.label; // Full address
      setOwnerDetails(prev => ({ ...prev, address: label }));
      setAddressSuggestions([]);
  };

  // Helper to generate contract number
  const generateContractNumber = (agencyCity: string = 'GLO') => {
      const year = new Date().getFullYear();
      const random = Math.floor(1000 + Math.random() * 9000);
      const cityCode = agencyCity.substring(0, 3).toUpperCase();
      return `CONT-${cityCode}-${year}-${random}`;
  };

  // NEW: Init from Proposal ID
  // Helper function to enrich signers from Yousign API response
  // Returns enriched signers with role, info, and signature date from audit trail
  // Uses existingSigners as base (from form data) and enriches with audit trail when available
  const enrichSignersFromYousign = async (
      yousignSigners: any[], 
      agencySigner: { firstName: string, lastName: string, email: string, phone: string },
      clientSigner: { firstName: string, lastName: string, email: string, phone: string },
      apiKey: string,
      env: 'sandbox' | 'live',
      signatureRequestId: string,
      existingSigners?: any[] // Optional: existing signers from DB/form data
  ): Promise<any[]> => {
      return await Promise.all(yousignSigners.map(async (signer: any, index: number) => {
          // Find existing signer data (from form or previous save)
          const existingSigner = existingSigners?.find((s: any) => s.id === signer.id);
          
          // Start with existing data or form data as base
          let signerInfo = existingSigner?.info || {
              first_name: index === 0 ? agencySigner.firstName : clientSigner.firstName,
              last_name: index === 0 ? agencySigner.lastName : clientSigner.lastName,
              email: index === 0 ? agencySigner.email : clientSigner.email,
              phone_number: index === 0 ? agencySigner.phone : clientSigner.phone
          };
          
          let signed_at = existingSigner?.signed_at || signer.signed_at || signer.signedAt || signer.signature_date || null;
          let role = existingSigner?.role || (index === 0 ? 'agency' : 'client');
          
          // Try to fetch audit trail to enrich with complete information (only if signed)
          if (signer.id && signer.status === 'signed') {
              try {
                  const auditTrail = await yousignService.getSignerAuditTrail(apiKey, env, signatureRequestId, signer.id);
                  if (auditTrail?.signer) {
                      // Enrich with audit trail data (most complete source)
                      signerInfo = {
                          first_name: auditTrail.signer.first_name || signerInfo.first_name,
                          last_name: auditTrail.signer.last_name || signerInfo.last_name,
                          email: auditTrail.signer.email_address || auditTrail.signer.email || signerInfo.email,
                          phone_number: auditTrail.signer.phone_number || auditTrail.signer.phone || signerInfo.phone_number
                      };
                      
                      // Get signature date from audit trail
                      if (auditTrail.signer.signature_process_completed_at) {
                          signed_at = auditTrail.signer.signature_process_completed_at;
                      } else if (auditTrail.signer.consent_given_at) {
                          signed_at = auditTrail.signer.consent_given_at;
                      }
                  }
              } catch (err: any) {
                  // If error is "not signed yet", that's expected - keep existing data
                  if (err.message && err.message.includes('not signed yet')) {
                      console.log('Signer not signed yet, keeping form data:', signer.id);
                  } else {
                      console.error('Error fetching audit trail for signer', signer.id, err);
                  }
              }
          }
          
          // Update role based on email if we have it
          const signerEmail = signerInfo.email || '';
          if (signerEmail) {
              if (agencySigner.email && signerEmail.toLowerCase() === agencySigner.email.toLowerCase()) {
                  role = 'agency';
              } else if (clientSigner.email && signerEmail.toLowerCase() === clientSigner.email.toLowerCase()) {
                  role = 'client';
              }
          }
          
          return {
              id: signer.id,
              role: role,
              status: signer.status,
              signed_at: signed_at,
              info: signerInfo
          };
      }));
  };

  const initCreationFromProposal = (propId: string, allDeals: Deal[], allProps: Property[], allProposals: Proposal[], allOwners: Owner[]) => {
      const proposal = allProposals.find(p => p.id === propId);
      if (!proposal) return;

      const deal = allDeals.find(d => d.id === proposal.dealId);
      if (!deal) return;

      setSelectedDealId(deal.id!);
      setSelectedProposalId(proposal.id);

      const property = allProps.find(p => p.id === deal.propertyId);
      const owner = allOwners.find(o => o.id === deal.ownerId);

      // Pre-fill owner details
      if (owner) {
          setOwnerDetails({
              civility: owner.civility || 'Monsieur',
              address: owner.address || ''
          });
      }

      // Preserve existing contract ID if we are editing
      const existingContractId = formData.id;

      setFormData({
          id: existingContractId, // Preserve ID
          dealId: deal.id,
          proposalId: proposal.id,
          type: deal.type, // Conciergerie or Sublet
          startDate: new Date().toISOString().split('T')[0],
          duration: '3', // Default numeric
          commissionRate: proposal.commissionRate, // Taken from Proposal
          status: 'DRAFT',
          signedCity: property?.city || '', // Initialize with Property City
          signedDate: new Date().toISOString().split('T')[0] // Initialize with Today
      });
      setViewMode('create');
      setSearchQuery('');
      setIsDropdownOpen(false);
  };

  const handleSave = async (status: 'DRAFT' | 'SENT' = 'DRAFT') => {
      if (!selectedDealId || !selectedProposalId) {
          toast.error("Une proposition doit être sélectionnée.");
          return;
      }
      
      const deal = deals.find(d => d.id === selectedDealId);
      if (!deal) {
          toast.error('Deal introuvable');
          return;
      }
      
      const agency = agencies.find(a => a.id === deal.agencyId);
      
      // Vérifier qu'une agence est associée au deal
      if (!agency) {
          toast.error('Aucune agence associée à ce deal. Veuillez sélectionner une agence pour ce deal avant de créer un contrat.');
          return;
      }
      
      // Vérifier que les infos de l'agence sont complètes
      if (!agency.city || !agency.email || !agency.legalStructure || !agency.siret || !agency.address) {
          toast.error('Les informations de l\'agence sont incomplètes. Veuillez compléter les informations de l\'agence dans les paramètres.');
          return;
      }
      
      // Generate contract number if missing
      const contractNumber = formData.contractNumber || generateContractNumber(agency.city);

      const newContract: Contract = {
          id: formData.id || crypto.randomUUID(),
          dealId: selectedDealId,
          proposalId: selectedProposalId,
          type: formData.type || PipelineType.CONCIERGE,
          status: status,
          contractNumber: contractNumber, // Added
          createdAt: formData.createdAt || new Date().toISOString(),
          startDate: formData.startDate!,
          duration: formData.duration!, // Save numeric value
          commissionRate: formData.commissionRate,
          rentAmount: formData.rentAmount,
          signedCity: formData.signedCity,
          signedDate: formData.signedDate,
          
          yousignSignatureRequestId: formData.yousignSignatureRequestId,
          yousignStatus: formData.yousignStatus
      };

      await storage.saveContract(newContract);
      
      // FORCE Update Owner Info in Database
      const owner = owners.find(o => o.id === deal?.ownerId);
      if (owner) {
          // Check if data is different
          const hasCivilityChanged = owner.civility !== ownerDetails.civility;
          const hasAddressChanged = owner.address !== ownerDetails.address;
          
          if (hasCivilityChanged || hasAddressChanged) {
              const updatedOwner = { 
                  ...owner, 
                  civility: ownerDetails.civility as any, 
                  address: ownerDetails.address 
              };
              
              const { error } = await storage.saveOwner(updatedOwner);
              if (!error) {
                  setOwners(prev => prev.map(o => o.id === owner.id ? updatedOwner : o));
                  toast.success("Fiche propriétaire mise à jour.");
              }
          }
      }

      // Update local state
      const updatedContracts = contracts.filter(c => c.id !== newContract.id);
      setContracts([newContract, ...updatedContracts]);
      setFormData(newContract);
      
      // Always update Deal with contractId to maintain the relationship
      if (deal) {
          // Add system comment for manual sending (only if not sent via Yousign)
          let updatedNotes = deal.notes || [];
          if (status === 'SENT' && !newContract.yousignSignatureRequestId) {
              const contractSentNote: Note = {
                  id: Date.now().toString(),
                  content: "📄 Contrat envoyé manuellement",
                  createdAt: new Date().toISOString(),
                  authorName: 'Système'
              };
              updatedNotes = [...updatedNotes, contractSentNote];
          }
          
          const updatedDeal = { 
              ...deal, 
              contractId: newContract.id,
              // Update stage only if status is SENT
              ...(status === 'SENT' && { stage: DealStage.CONTRACT_SENT }),
              notes: updatedNotes
          };
          await storage.saveDeal(updatedDeal);
          setDeals(prev => prev.map(d => d.id === deal.id ? updatedDeal : d));
          
          // Notify parent component to refresh data
          if (onDealUpdated) onDealUpdated();
      }
      
      if (status === 'SENT') {
          toast.success("Contrat marqué comme envoyé !");
      } else {
          toast.success("Contrat sauvegardé (Brouillon).");
      }
      
      setViewMode('preview');
  };

  const handleSignContract = async () => {
      if (!formData.id || !selectedDealId) return;
      
      if (await confirm({
          title: "Valider la signature ?",
          message: "Cela marquera le deal comme GAGNÉ et passera le bien en GESTION. Cette action est irréversible.",
          confirmText: "Oui, signer",
          danger: false
      })) {
          // Manually signing
          performSigning("🖊️ Contrat marqué comme signé manuellement.");
      }
  };

  // Helper function to finalize signature (used by manual and Yousign flow)
  const performSigning = async (noteContent: string, signedFile?: Blob, contractOverride?: Contract) => {
      const deal = deals.find(d => d.id === selectedDealId);
      const property = properties.find(p => p.id === deal?.propertyId);
      const contractData = contractOverride || formData as Contract;
      
      if (deal && property) {
          const { contract, deal: updatedDeal } = await storage.signContract(
              contractData, 
              deal, 
              property,
              noteContent,
              signedFile
          );
          setFormData(contract);
          setContracts(prev => prev.map(c => c.id === contract.id ? contract : c));
          setDeals(prev => prev.map(d => d.id === updatedDeal.id ? updatedDeal : d));
          
          // Notify parent component to refresh data
          if (onDealUpdated) onDealUpdated();
          
          toast.success("Contrat signé ! Félicitations !");
      }
  };

  // --- PDF GENERATION HELPERS ---
  const preparePdfClone = () => {
      const element = document.getElementById('contract-container-full');
      if (!element) return null;

      const clone = element.cloneNode(true) as HTMLElement;
      
      clone.classList.remove('mx-auto', 'shadow-2xl', 'flex', 'flex-col', 'gap-8', 'items-center');
      clone.style.margin = '0';
      clone.style.padding = '0';
      clone.style.boxShadow = 'none';
      clone.style.backgroundColor = '#ffffff';
      clone.style.width = '210mm'; 
      clone.style.display = 'block'; 

      const pages = clone.querySelectorAll('.a4-page');
      pages.forEach((p, index) => {
          const el = p as HTMLElement;
          el.style.marginBottom = '0'; 
          el.style.boxShadow = 'none';
          el.style.margin = '0'; 
          el.style.height = '295mm';
          el.style.maxHeight = '295mm';
          el.style.overflow = 'hidden';
          if (index < pages.length - 1) el.style.pageBreakAfter = 'always';
          else el.style.pageBreakAfter = 'avoid';
      });

      return clone;
  };

  const getPdfOptions = (reference: string) => ({
      margin: 0, 
      filename: `Contrat_conciergerie-${reference}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
          scale: 2, 
          useCORS: true, 
          letterRendering: true, 
          backgroundColor: '#ffffff', 
          logging: false, 
          scrollY: 0,
          // Important: préserver le texte pour que Yousign puisse détecter les Smart Anchors
          textRendering: true
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
      pagebreak: { mode: ['css', 'legacy'] }
  });

  const getPdfBlob = async (): Promise<Blob | null> => {
      const clone = preparePdfClone();
      if (!clone) return null;

      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '0';
      wrapper.style.width = '210mm'; 
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);
      
      try {
          // @ts-ignore
          if (window.html2pdf) {
              // @ts-ignore
              return await window.html2pdf()
                .set(getPdfOptions(formData.contractNumber || 'REF'))
                .from(clone)
                .output('blob');
          }
          return null;
      } catch (e) {
          console.error(e);
          return null;
      } finally {
          document.body.removeChild(wrapper);
      }
  };

  const generatePDF = async () => {
      // Vérifier que l'agence est présente
      const deal = deals.find(d => d.id === selectedDealId);
      const agency = agencies.find(a => a.id === deal?.agencyId);
      
      if (!agency) {
          toast.error('Aucune agence associée à ce deal. Impossible de générer le PDF.');
          return;
      }
      
      if (!agency.city || !agency.email || !agency.legalStructure || !agency.siret || !agency.address) {
          toast.error('Les informations de l\'agence sont incomplètes. Veuillez compléter les informations de l\'agence dans les paramètres.');
          return;
      }
      
      const clone = preparePdfClone();
      if (!clone) return;
      
      setIsDownloadingPdf(true);
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '0';
      wrapper.style.width = '210mm'; 
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);
      
      try {
          // @ts-ignore
          if (window.html2pdf) {
              // @ts-ignore
              await window.html2pdf().set(getPdfOptions(formData.contractNumber || 'REF')).from(clone).save();
              toast.success("PDF Téléchargé");
          }
      } catch (e) {
          console.error(e);
          toast.error("Erreur génération PDF.");
      } finally {
          document.body.removeChild(wrapper);
          setIsDownloadingPdf(false);
      }
  };

  // --- YOUSIGN INTEGRATION LOGIC ---

  const handleOpenYousignModal = () => {
      if (!contractAgency?.yousignApiKey) {
          toast.error("Veuillez configurer la clé API Yousign dans l'administration de l'agence.");
          return;
      }
      
      // 1. CLIENT INFO (From Owner or Previous Input)
      // Check if we already have saved inputs
      if (!clientSigner.email) {
          setClientSigner({
              firstName: selectedOwner?.firstName || '',
              lastName: selectedOwner?.lastName || '',
              email: selectedOwner?.email || '',
              phone: selectedOwner?.phone || ''
          });
      }

      // 2. AGENCY INFO (From User Profile or Agency Contact or Previous Input)
      if (!agencySigner.email) {
          setAgencySigner({
              firstName: userProfile?.firstName || 'Service',
              lastName: userProfile?.lastName || 'Gestion',
              email: userProfile?.email || contractAgency.email || '',
              phone: contractAgency.phone || ''
          });
      }

      setIsYousignModalOpen(true);
  };

  const handleSendYousign = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSendingYousign(true);

      try {
          const apiKey = contractAgency?.yousignApiKey;
          const env = contractAgency?.yousignEnvironment || 'sandbox'; // Use saved env or default to sandbox
          if (!apiKey) throw new Error("Clé API manquante");

          // 1. Generate PDF
          const pdfBlob = await getPdfBlob();
          if (!pdfBlob) throw new Error("Erreur génération PDF");

          // 2. Initiate Request
          const initRes = await yousignService.initiateSignatureRequest(
              apiKey, 
              env, 
              `Contrat ${formData.contractNumber}`, 
              contractAgency // Pass agency details for custom email text
          );
          const requestId = initRes.id;

          // 3. Upload Document
          const docRes = await yousignService.uploadDocument(apiKey, env, requestId, pdfBlob, `Contrat_${formData.contractNumber}.pdf`);
          const docId = docRes.id;

          // 4. Add Signers (Two Parties)
          const pageCount = document.querySelectorAll('.a4-page').length || 9;
          
          // A. Agency Signer
          await yousignService.addSigner(
              apiKey, env, requestId, docId, 
              agencySigner, 'agency', pageCount
          );

          // B. Client Signer
          await yousignService.addSigner(
              apiKey, env, requestId, docId, 
              clientSigner, 'client', pageCount
          );

          // 5. Activate Request
          await yousignService.activateRequest(apiKey, env, requestId);

          // 6. Get signers from API to store enriched data
          // We need to get the signers with their IDs from Yousign
          const statusRes = await yousignService.getSignatureRequestStatus(apiKey, env, requestId);
          
          // 7. Create initial signers structure with form data (before API enrichment)
          // This ensures we have the info even if audit trail is not available yet
          const initialSigners = (statusRes.signers || []).map((signer: any, index: number) => {
              const role = index === 0 ? 'agency' : 'client';
              const formDataSigner = role === 'agency' ? agencySigner : clientSigner;
              
              return {
                  id: signer.id,
                  role: role,
                  status: signer.status,
                  signed_at: signer.signed_at || signer.signedAt || signer.signature_date || null,
                  info: {
                      first_name: formDataSigner.firstName,
                      last_name: formDataSigner.lastName,
                      email: formDataSigner.email,
                      phone_number: formDataSigner.phone
                  }
              };
          });
          
          // 8. Enrich signers with audit trail data when available (async, will update later)
          const enrichedSigners = await enrichSignersFromYousign(
              statusRes.signers || [],
              agencySigner,
              clientSigner,
              apiKey,
              env,
              requestId,
              initialSigners // Pass initial signers as base
          );

          // 9. Save State with enriched signers
          const updatedContract: Contract = {
              ...formData as Contract,
              yousignSignatureRequestId: requestId,
              yousignStatus: 'pending', // Global status
              status: 'SENT',
              contentJson: {
                  signers: enrichedSigners
              }
          };
          
          await storage.saveContract(updatedContract);
          setFormData(updatedContract);
          
          // Update Deal with contractId and stage
          const deal = deals.find(d => d.id === selectedDealId);
          if (deal) {
              // Add system comment for Yousign
              const contractSentNote: Note = {
                  id: Date.now().toString(),
                  content: "📄 Contrat envoyé via Yousign",
                  createdAt: new Date().toISOString(),
                  authorName: 'Système'
              };
              
              const updatedDeal = { 
                  ...deal, 
                  contractId: updatedContract.id,
                  stage: DealStage.CONTRACT_SENT,
                  notes: [...(deal.notes || []), contractSentNote]
              };
              await storage.saveDeal(updatedDeal);
              setDeals(prev => prev.map(d => d.id === deal.id ? updatedDeal : d));
              
              // Notify parent component to refresh data
              if (onDealUpdated) onDealUpdated();
          }
          
          // Recharger les contrats pour mettre à jour la liste
          const allContracts = await storage.getContracts();
          const filteredContracts = selectedAgencyId 
              ? allContracts.filter(c => {
                  const deal = deals.find(d => d.id === c.dealId);
                  return deal?.agencyId === selectedAgencyId;
              })
              : allContracts;
          setContracts(filteredContracts);
          
          setIsYousignModalOpen(false);
          toast.success(`Contrat envoyé à ${clientSigner.email} et ${agencySigner.email} !`);
          
          // Trigger immediate status refresh to get signer IDs
          setTimeout(() => handleRefreshYousignStatus(updatedContract), 1000);

      } catch (err: any) {
          console.error("Yousign Error:", err);
          toast.error("Erreur Yousign: " + (err.message || 'Inconnue'));
      } finally {
          setIsSendingYousign(false);
      }
  };

  const handleRefreshYousignStatus = async (contractOverride?: Contract) => {
      const contractToCheck = contractOverride || formData;
      if (!contractToCheck.yousignSignatureRequestId || !contractAgency?.yousignApiKey) return;
      const env = contractAgency?.yousignEnvironment || 'sandbox';
      const apiKey = contractAgency.yousignApiKey;
      
      setIsRefreshingYousign(true);
      try {
          const res = await yousignService.getSignatureRequestStatus(apiKey, env, contractToCheck.yousignSignatureRequestId);
          const globalStatus = res.status; // 'done', 'expired', 'refused' ...
          
          // DEFENSIVE: Ensure contentJson is treated as object before spreading
          let currentContentJson = contractToCheck.contentJson || {};
          if (typeof currentContentJson === 'string') {
              try { currentContentJson = JSON.parse(currentContentJson); } catch {}
          }
          
          // Get existing signers to preserve their info and role
          const existingSigners = currentContentJson.signers || [];
          
          // Reconstruct agency and client signers from existing data or fallback to state
          let agencySignerData = { firstName: '', lastName: '', email: '', phone: '' };
          let clientSignerData = { firstName: '', lastName: '', email: '', phone: '' };
          
          existingSigners.forEach((s: any) => {
              if (s.role === 'agency') {
                  agencySignerData = {
                      firstName: s.info?.first_name || '',
                      lastName: s.info?.last_name || '',
                      email: s.info?.email || '',
                      phone: s.info?.phone_number || ''
                  };
              } else if (s.role === 'client') {
                  clientSignerData = {
                      firstName: s.info?.first_name || '',
                      lastName: s.info?.last_name || '',
                      email: s.info?.email || '',
                      phone: s.info?.phone_number || ''
                  };
              }
          });
          
          // Fallback to state if not in contentJson (for backward compatibility)
          if (!agencySignerData.email && agencySigner.email) {
              agencySignerData = agencySigner;
          }
          if (!clientSignerData.email && clientSigner.email) {
              clientSignerData = clientSigner;
          }
          
          // Enrich signers from Yousign API response
          // Use existing signers as base and enrich with API data
          let enrichedSigners = existingSigners; // Default to existing
          
          if (res.signers && Array.isArray(res.signers) && res.signers.length > 0) {
              // Enrich signers from API, using existing signers as base
              enrichedSigners = await enrichSignersFromYousign(
                  res.signers,
                  agencySignerData,
                  clientSignerData,
                  apiKey,
                  env,
                  contractToCheck.yousignSignatureRequestId,
                  existingSigners // Pass existing as base
              );
              console.log('Enriched signers from Yousign API:', enrichedSigners);
          } else {
              console.log('No signers from API or empty array, keeping existing signers:', existingSigners);
          }
          
          // Update contentJson with enriched signers
          const updatedContentJson = {
              signers: enrichedSigners
          };
          
          setSignersStatus(enrichedSigners);
          
          // ALWAYS save enriched data with signature dates to DB
          // This ensures signed_at dates from audit trails are persisted and signers are always up to date
          const updatedContract = { 
              ...contractToCheck as Contract, 
              yousignStatus: globalStatus,
              contentJson: updatedContentJson
          };
          
          // Always save to DB to persist the enriched signers data
          await storage.saveContract(updatedContract);
          
          // Update local state if not 'done' (if 'done', performSigning will handle additional state update)
          if (globalStatus !== 'done') {
              setFormData(updatedContract);
              if(!contractOverride) toast.info(`Statut mis à jour : ${globalStatus}`);
          }

          if (globalStatus !== contractToCheck.yousignStatus) {
              if (globalStatus === 'done') {
                  // 1. Download Signed File
                  toast.info("Signature terminée ! Téléchargement du contrat signé...");
                  let signedFile;
                  try {
                      signedFile = await yousignService.downloadSignedDocument(apiKey, env, contractToCheck.yousignSignatureRequestId);
                  } catch (downloadErr) {
                      console.error("Erreur téléchargement PDF signé", downloadErr);
                      toast.error("Impossible de télécharger le PDF signé.");
                  }

                  // 2. Perform Signing (Update Deal, Property, Contract + Upload)
                  // Pass the updated contract object with signers info (including signed_at dates) to performSigning
                  const finalContract = { 
                      ...updatedContract, // Use updatedContract which already has enriched signersStatus with signed_at dates
                      yousignStatus: globalStatus
                  };
                  
                  const signedDate = res.created_at ? new Date(res.created_at).toLocaleDateString() : new Date().toLocaleDateString();
                  await performSigning(`✅ Signé via Yousign le ${signedDate}`, signedFile, finalContract);
                  
                  // Update local form state (performSigning handles db update)
                  setFormData(prev => ({ ...prev, yousignStatus: globalStatus }));
              }
          } 
      } catch (e) {
          console.error(e);
          toast.error("Impossible de vérifier le statut.");
      } finally {
          setIsRefreshingYousign(false);
      }
  };

  const handleResendInvite = async (signerId: string) => {
      if (!formData.yousignSignatureRequestId || !contractAgency?.yousignApiKey) return;
      const env = contractAgency?.yousignEnvironment || 'sandbox';
      const apiKey = contractAgency.yousignApiKey;

      try {
          await yousignService.sendReminder(apiKey, env, formData.yousignSignatureRequestId, signerId);
          toast.success("Invitation renvoyée au signataire.");
      } catch (e: any) {
          console.error("Resend error", e);
          toast.error("Erreur lors du renvoi : " + e.message);
      }
  };

  // --- RENDER HELPERS ---
  const selectedDeal = deals.find(d => d.id === selectedDealId);
  const selectedOwner = owners.find(o => o.id === selectedDeal?.ownerId);
  const selectedProperty = properties.find(p => p.id === selectedDeal?.propertyId);
  const contractAgency = agencies.find(a => a.id === selectedDeal?.agencyId);
  const selectedProposal = proposals.find(p => p.id === selectedProposalId);

  // Variables dynamiques - toutes les infos doivent venir de l'agence, pas de valeurs par défaut
  const agencyName = contractAgency?.name || '';
  const agencyLegalStruct = contractAgency?.legalStructure || '';
  const agencyCapital = contractAgency?.capital || 0;
  const agencyAddress = contractAgency?.address || '';
  const agencyRCS = contractAgency?.rcsCity || contractAgency?.city || '';
  const agencySIRET = contractAgency?.siret || '';
  const agencyEmail = contractAgency?.email || '';
  const representativeName = userProfile ? `${userProfile.firstName} ${userProfile.lastName}` : "Le Dirigeant";
  const setupFee = selectedProposal?.setupFee !== undefined ? selectedProposal.setupFee : 249;
  const currentCommission = selectedProposal ? selectedProposal.commissionRate : formData.commissionRate || 25;
  const isContractSaved = contracts.some(c => c.id === formData.id);
  const isLocked = formData.status === 'SIGNED';

  // Filter valid proposals for the dropdown
  const availableProposals = proposals.filter(p => p.status === 'ACCEPTED' || p.status === 'SENT');
  const filteredProposals = availableProposals.filter(p => {
      const hasContract = contracts.some(c => c.proposalId === p.id);
      if (hasContract) return false; 
      const deal = deals.find(d => d.id === p.dealId);
      const o = owners.find(x => x.id === deal?.ownerId);
      const prop = properties.find(x => x.id === deal?.propertyId);
      const search = searchQuery.toLowerCase();
      return (
          p.reference?.toLowerCase().includes(search) ||
          o?.firstName.toLowerCase().includes(search) ||
          o?.lastName.toLowerCase().includes(search) ||
          prop?.city.toLowerCase().includes(search)
      );
  }).slice(0, 10);

  // --- VIEWS ---

  if (viewMode === 'list') {
      return (
          <div className="p-8 h-full overflow-y-auto">
              <div className="flex justify-between items-center mb-8">
                  <div>
                      <h2 className="text-2xl font-bold text-slate-900">Contrats</h2>
                      <p className="text-slate-500">Gérez les mandats de gestion et baux.</p>
                  </div>
                  <button 
                      onClick={() => {
                          toast.info('Veuillez d\'abord sélectionner un deal avec une proposition acceptée depuis le Pipeline pour créer un contrat.');
                      }} 
                      className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center font-bold shadow-sm"
                      title="Sélectionnez un deal avec une proposition acceptée depuis le Pipeline pour créer un contrat"
                  >
                      <Plus className="w-4 h-4 mr-2" /> Nouveau Contrat
                  </button>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <table className="w-full text-left">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-semibold">
                          <tr>
                              <th className="p-4">Référence</th>
                              <th className="p-4">Type</th>
                              <th className="p-4">Client</th>
                              <th className="p-4">Bien</th>
                              <th className="p-4 text-center">Statut</th>
                              <th className="p-4 text-right">Actions</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                          {contracts.map(contract => {
                              const deal = deals.find(d => d.id === contract.dealId);
                              const owner = owners.find(o => o.id === deal?.ownerId);
                              const property = properties.find(p => p.id === deal?.propertyId);
                              
                              return (
                                  <tr key={contract.id} className="hover:bg-slate-50 transition-colors">
                                      <td className="p-4">
                                          <div className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded w-fit">
                                              {contract.contractNumber || '---'}
                                          </div>
                                          <div className="text-xs text-slate-400 mt-1">{new Date(contract.createdAt).toLocaleDateString()}</div>
                                          {(contract.yousignStatus === 'pending' || contract.yousignStatus === 'ongoing') && (() => {
                                              const signers = (contract.contentJson as any)?.signers || [];
                                              const signedCount = signers.filter((s: any) => s.status === 'signed').length;
                                              const totalCount = Math.max(signers.length, 2); // Au minimum 2 signataires (agence + client)
                                              return (
                                                  <span className="text-[9px] text-amber-600 bg-amber-50 px-1 rounded border border-amber-100 mt-1 inline-block">
                                                      Signature en cours {signedCount}/{totalCount}
                                                  </span>
                                              );
                                          })()}
                                      </td>
                                      <td className="p-4">
                                          <span className={`px-2 py-1 rounded border text-xs font-bold uppercase ${contract.type === PipelineType.CONCIERGE ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                                              {contract.type === PipelineType.CONCIERGE ? 'Conciergerie' : 'Sous-Loc'}
                                          </span>
                                      </td>
                                      <td className="p-4">
                                          <div className="font-bold text-slate-900">{owner?.firstName} {owner?.lastName}</div>
                                      </td>
                                      <td className="p-4 text-sm text-slate-500">
                                          {property?.address}, {property?.city}
                                      </td>
                                      <td className="p-4 text-center">
                                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${contract.status === 'SIGNED' ? 'bg-green-100 text-green-700' : contract.status === 'SENT' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                                              {contract.status === 'SIGNED' ? 'SIGNÉ' : contract.status === 'SENT' ? 'ENVOYÉ' : 'BROUILLON'}
                                          </span>
                                      </td>
                                      <td className="p-4 text-right">
                                          <div className="flex items-center justify-end gap-2">
                                              {/* Action: View Managed Property if Signed */}
                                              {contract.status === 'SIGNED' && onNavigateToManaged && deal && (
                                                  <button 
                                                      onClick={() => onNavigateToManaged(deal.propertyId)} 
                                                      className="text-green-600 hover:text-green-800 p-1.5 hover:bg-green-50 rounded"
                                                      title="Voir le bien géré"
                                                  >
                                                      <Key className="w-4 h-4" />
                                                  </button>
                                              )}
                                              <button onClick={() => { 
                                                  // Strip text to get number only
                                                  let cleanDuration = contract.duration || '3';
                                                  cleanDuration = cleanDuration.replace(/[^0-9]/g, '');
                                                  setFormData({...contract, duration: cleanDuration}); 
                                                  setSelectedDealId(contract.dealId);
                                                  if (contract.proposalId) setSelectedProposalId(contract.proposalId);
                                                  
                                                  // Load persisted signers status
                                                  // DEFENSIVE PARSING
                                                  let contentObj = contract.contentJson;
                                                  if (typeof contentObj === 'string') {
                                                      try { contentObj = JSON.parse(contentObj); } catch {}
                                                  }
                                                  
                                                  // Load signers from new unified structure
                                                  if(contentObj?.signers && Array.isArray(contentObj.signers)) {
                                                      setSignersStatus(contentObj.signers);
                                                      
                                                      // Restore signers inputs for editing
                                                      const agencySignerData = contentObj.signers.find((s: any) => s.role === 'agency');
                                                      const clientSignerData = contentObj.signers.find((s: any) => s.role === 'client');
                                                      
                                                      if (agencySignerData?.info) {
                                                          setAgencySigner({
                                                              firstName: agencySignerData.info.first_name || '',
                                                              lastName: agencySignerData.info.last_name || '',
                                                              email: agencySignerData.info.email || '',
                                                              phone: agencySignerData.info.phone_number || ''
                                                          });
                                                      }
                                                      if (clientSignerData?.info) {
                                                          setClientSigner({
                                                              firstName: clientSignerData.info.first_name || '',
                                                              lastName: clientSignerData.info.last_name || '',
                                                              email: clientSignerData.info.email || '',
                                                              phone: clientSignerData.info.phone_number || ''
                                                          });
                                                      }
                                                  } else if (contentObj?.signersStatus && contentObj?.submittedSigners) {
                                                      // Backward compatibility: migrate old structure to new
                                                      const migratedSigners = contentObj.signersStatus.map((signer: any, index: number) => {
                                                          const submittedData = index === 0 ? contentObj.submittedSigners.agency : contentObj.submittedSigners.client;
                                                          return {
                                                              id: signer.id,
                                                              role: index === 0 ? 'agency' : 'client',
                                                              status: signer.status,
                                                              signed_at: signer.signed_at || signer.signedAt || signer.signature_date,
                                                              info: {
                                                                  first_name: submittedData?.firstName || signer.info?.first_name || '',
                                                                  last_name: submittedData?.lastName || signer.info?.last_name || '',
                                                                  email: submittedData?.email || signer.info?.email || '',
                                                                  phone_number: submittedData?.phone || signer.info?.phone_number || ''
                                                              }
                                                          };
                                                      });
                                                      setSignersStatus(migratedSigners);
                                                  }

                                                  setViewMode('preview'); 
                                                  // Refresh Yousign status immediately ONLY if relevant (pending/ongoing)
                                                  // Prevent spamming API on signed contracts
                                                  if (contract.yousignStatus === 'pending' || contract.yousignStatus === 'ongoing') {
                                                      setTimeout(() => handleRefreshYousignStatus(contract), 500);
                                                  }
                                                }} className="text-amber-600 hover:text-amber-800 font-medium text-sm hover:underline">
                                                  Ouvrir
                                              </button>
                                          </div>
                                      </td>
                                  </tr>
                              );
                          })}
                          {contracts.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">Aucun contrat créé.</td></tr>}
                      </tbody>
                  </table>
              </div>
          </div>
      );
  }

  // --- CREATE / EDIT ---
  if (viewMode === 'create') {
      return (
          <div className="p-8 h-full overflow-y-auto bg-slate-50">
              <button onClick={() => setViewMode('list')} className="mb-6 text-slate-500 hover:text-slate-800 flex items-center font-medium"><ArrowLeft className="w-4 h-4 mr-2" /> Retour liste</button>
              
              <div className="max-w-3xl mx-auto">
                  <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                      <div className="p-8 border-b border-slate-100 bg-slate-50">
                          <h2 className="text-2xl font-bold text-slate-900">Édition du Contrat</h2>
                          <p className="text-slate-500">Sélectionnez une proposition validée pour commencer.</p>
                      </div>

                      <div className="p-8 space-y-8">
                          {/* 1. SELECTION DE LA PROPOSITION */}
                          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" style={{ zIndex: 30 }}>
                              <h3 className="font-bold text-lg text-slate-800 mb-6 flex items-center">
                                  <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center mr-3 text-sm">1</span>
                                  Proposition de Référence
                              </h3>
                              
                              {/* If selected, show card */}
                              {selectedProposalId && selectedDeal ? (
                                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between animate-in fade-in">
                                      <div className="flex items-center">
                                          <div className="w-12 h-12 bg-amber-200 text-amber-700 rounded-full flex items-center justify-center font-bold mr-4 shadow-sm border-2 border-white">
                                              <FileText className="w-6 h-6" />
                                          </div>
                                          <div>
                                              <p className="font-bold text-slate-900 text-lg">
                                                  {selectedProposal?.reference}
                                                  {selectedProposal?.status === 'ACCEPTED' && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded border border-green-200 font-bold uppercase">Validée</span>}
                                              </p>
                                              <p className="text-amber-700 text-sm flex items-center mt-1">
                                                  <User className="w-3.5 h-3.5 mr-1" />
                                                  {selectedOwner?.firstName} {selectedOwner?.lastName}
                                                  <span className="mx-2">•</span>
                                                  <Home className="w-3.5 h-3.5 mr-1" />
                                                  {selectedProperty?.city}
                                              </p>
                                          </div>
                                      </div>
                                      {!isContractSaved && (
                                          <button 
                                              onClick={() => { setSelectedDealId(''); setSelectedProposalId(''); setSearchQuery(''); }} 
                                              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-full transition-colors"
                                              title="Changer de proposition"
                                          >
                                              <X className="w-5 h-5" />
                                          </button>
                                      )}
                                  </div>
                              ) : (
                                  /* Search Input */
                                  <div className="relative" ref={searchRef}>
                                      <div className="relative">
                                          <Search className="absolute left-4 top-3.5 w-5 h-5 text-slate-400" />
                                          <input 
                                              type="text" 
                                              className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none shadow-sm transition-all text-slate-900 bg-slate-50 hover:bg-white focus:bg-white"
                                              placeholder="Rechercher une proposition (Référence, Client, Ville)..."
                                              value={searchQuery}
                                              onChange={(e) => { setSearchQuery(e.target.value); setIsDropdownOpen(true); }}
                                              onFocus={() => setIsDropdownOpen(true)}
                                          />
                                          <div className="absolute right-4 top-3.5 pointer-events-none text-slate-400">
                                              <ChevronDown className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                          </div>
                                      </div>

                                      {/* Dropdown Results */}
                                      {isDropdownOpen && (
                                          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-80 overflow-y-auto animate-in fade-in zoom-in-95 duration-100">
                                              {filteredProposals.length > 0 ? (
                                                  <div className="divide-y divide-slate-100">
                                                      {filteredProposals.map(prop => {
                                                          const deal = deals.find(d => d.id === prop.dealId);
                                                          const o = owners.find(x => x.id === deal?.ownerId);
                                                          const p = properties.find(x => x.id === deal?.propertyId);
                                                          return (
                                                              <div 
                                                                  key={prop.id} 
                                                                  onClick={() => initCreationFromProposal(prop.id, deals, properties, proposals, owners)}
                                                                  className="p-3 hover:bg-amber-50 cursor-pointer flex items-center justify-between group transition-colors"
                                                              >
                                                                  <div className="flex items-center overflow-hidden">
                                                                      <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-3 shrink-0 group-hover:bg-white group-hover:text-amber-500 transition-colors">
                                                                          <FileText className="w-4 h-4" />
                                                                      </div>
                                                                      <div className="min-w-0">
                                                                          <p className="text-sm font-bold text-slate-800 truncate group-hover:text-amber-700">
                                                                              {prop.reference}
                                                                          </p>
                                                                          <p className="text-xs text-slate-500 truncate flex items-center">
                                                                              {o?.firstName} {o?.lastName} • {p?.city}
                                                                          </p>
                                                                      </div>
                                                                  </div>
                                                                  <span className={`text-[10px] px-2 py-1 rounded font-medium ml-2 whitespace-nowrap ${prop.status === 'ACCEPTED' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                                                      {prop.status === 'ACCEPTED' ? 'VALIDÉE' : 'ENVOYÉE'}
                                                                  </span>
                                                              </div>
                                                          );
                                                      })}
                                                  </div>
                                              ) : (
                                                  <div className="p-8 text-center text-slate-500">
                                                      <p>Aucune proposition trouvée.</p>
                                                      <p className="text-xs mt-1 text-slate-400">Vérifiez qu'elle est bien marquée comme "Envoyée" ou "Acceptée" et qu'aucun contrat n'est déjà lié.</p>
                                                  </div>
                                              )}
                                          </div>
                                      )}
                                  </div>
                              )}
                          </div>

                          {/* 2. PARTIES & ADRESSE */}
                          {selectedDealId && (
                              <div className="animate-in fade-in slide-in-from-bottom-4">
                                  <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center">
                                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-2 text-xs">2</span>
                                      Informations Propriétaire
                                  </h3>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                      <div className="md:col-span-1">
                                          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Civilité</label>
                                          {selectedOwner?.civility ? (
                                              <div className="w-full p-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-600 cursor-not-allowed">
                                                  {selectedOwner.civility}
                                                  <span className="block text-[9px] text-blue-600 font-medium mt-1">Donnée existante</span>
                                              </div>
                                          ) : (
                                              <select 
                                                disabled={isLocked}
                                                className="w-full p-3 border border-slate-300 rounded-xl bg-white text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
                                                value={ownerDetails.civility}
                                                onChange={e => setOwnerDetails({...ownerDetails, civility: e.target.value})}
                                              >
                                                  <option value="Monsieur">Monsieur</option>
                                                  <option value="Madame">Madame</option>
                                              </select>
                                          )}
                                      </div>
                                      <div className="md:col-span-2 relative">
                                          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Adresse Postale (Domicile)</label>
                                          
                                          {selectedOwner?.address ? (
                                              <div className="w-full p-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-600 cursor-not-allowed flex items-center">
                                                  <MapPin className="w-5 h-5 text-slate-400 mr-3 shrink-0" />
                                                  <span className="truncate">{selectedOwner.address}</span>
                                                  <span className="ml-auto text-[9px] text-blue-600 font-medium whitespace-nowrap pl-2">Donnée existante</span>
                                              </div>
                                          ) : (
                                              <>
                                                  <div className="relative">
                                                      <MapPin className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
                                                      <input 
                                                        disabled={isLocked}
                                                        type="text" 
                                                        className="w-full pl-10 p-3 border border-slate-300 rounded-xl bg-white text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none placeholder-slate-400 disabled:bg-slate-50 disabled:text-slate-500"
                                                        placeholder="Rechercher une adresse..."
                                                        value={ownerDetails.address}
                                                        onChange={e => handleAddressSearch(e.target.value)}
                                                      />
                                                      {addressSuggestions.length > 0 && (
                                                          <ul className="absolute z-10 w-full bg-white border border-slate-200 rounded-xl mt-1 shadow-xl max-h-48 overflow-auto">
                                                              {addressSuggestions.map((s, i) => (
                                                                  <li key={i} onClick={() => selectAddress(s)} className="p-3 hover:bg-slate-50 cursor-pointer text-sm border-b border-slate-50 last:border-0 text-slate-900">
                                                                      <span className="font-bold block">{s.properties.name}</span> 
                                                                      <span className="text-slate-500 text-xs">{s.properties.postcode} {s.properties.city}</span>
                                                                  </li>
                                                              ))}
                                                          </ul>
                                                      )}
                                                  </div>
                                                  {!isLocked && <p className="text-[10px] text-amber-600 mt-1 font-medium italic">
                                                      Cette adresse sera enregistrée dans la fiche propriétaire à la sauvegarde.
                                                  </p>}
                                              </>
                                          )}
                                      </div>
                                  </div>

                                  {/* Informations du Bien (Read-Only) */}
                                  {selectedProperty && (
                                      <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                                          <h4 className="text-xs font-bold text-slate-500 uppercase mb-3 flex items-center">
                                              <Home className="w-3.5 h-3.5 mr-1.5" />
                                              Informations du Bien (Lecture Seule)
                                          </h4>
                                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                              <div>
                                                  <p className="text-[10px] text-slate-400 font-bold uppercase mb-0.5">Type</p>
                                                  <p className="text-sm font-bold text-slate-800">{selectedProperty.type}</p>
                                              </div>
                                              <div>
                                                  <p className="text-[10px] text-slate-400 font-bold uppercase mb-0.5">Surface</p>
                                                  <div className="flex items-center text-sm font-bold text-slate-800">
                                                      <Ruler className="w-3.5 h-3.5 mr-1 text-slate-400" />
                                                      {selectedProperty.surface} m²
                                                  </div>
                                              </div>
                                              <div>
                                                  <p className="text-[10px] text-slate-400 font-bold uppercase mb-0.5">Chambres</p>
                                                  <div className="flex items-center text-sm font-bold text-slate-800">
                                                      <BedDouble className="w-3.5 h-3.5 mr-1 text-slate-400" />
                                                      {selectedProperty.bedrooms || 0}
                                                  </div>
                                              </div>
                                              <div>
                                                  <p className="text-[10px] text-slate-400 font-bold uppercase mb-0.5">Capacité</p>
                                                  <div className="flex items-center text-sm font-bold text-slate-800">
                                                      <Users className="w-3.5 h-3.5 mr-1 text-slate-400" />
                                                      {selectedProperty.maxGuests} pers.
                                                  </div>
                                              </div>
                                          </div>
                                          <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500">
                                              <MapPin className="w-3 h-3 inline mr-1" />
                                              {selectedProperty.address}, {selectedProperty.city}
                                          </div>
                                      </div>
                                  )}
                              </div>
                          )}

                          {/* 3. TERMES DU CONTRAT */}
                          {selectedDealId && (
                              <div className="animate-in fade-in slide-in-from-bottom-8">
                                  <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center">
                                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-2 text-xs">3</span>
                                      Termes & Conditions
                                  </h3>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-6 rounded-xl border border-slate-100">
                                      <div>
                                          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Date de début des prestations</label>
                                          <div className="relative">
                                              <Calendar className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
                                              <input disabled={isLocked} type="date" className="w-full pl-10 p-3 border border-slate-300 rounded-xl bg-white text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none disabled:bg-slate-100 disabled:text-slate-500" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} />
                                          </div>
                                      </div>
                                      <div>
                                          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Durée du préavis pour résiliation</label>
                                          <div className="relative">
                                              <input 
                                                disabled={isLocked}
                                                type="number" 
                                                min="1"
                                                step="1"
                                                className="w-full p-3 pr-12 border border-slate-300 rounded-xl bg-white text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none disabled:bg-slate-100 disabled:text-slate-500" 
                                                value={formData.duration} 
                                                onChange={e => setFormData({...formData, duration: e.target.value})} 
                                              />
                                              <span className="absolute right-3 top-3.5 text-slate-400 font-bold text-sm pl-1">Mois</span>
                                          </div>
                                      </div>

                                      {formData.type === PipelineType.CONCIERGE ? (
                                          <>
                                              <div>
                                                  <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Commission (%)</label>
                                                  <div className="relative">
                                                      <input 
                                                        type="number" 
                                                        readOnly
                                                        className="w-full p-3 pr-8 border border-slate-200 rounded-xl bg-slate-100 text-slate-500 font-bold outline-none cursor-not-allowed" 
                                                        value={currentCommission} // From Proposal
                                                      />
                                                      <span className="absolute right-3 top-3.5 text-slate-400 font-bold">%</span>
                                                  </div>
                                                  <p className="text-[10px] text-purple-600 font-medium mt-1">Fixé dans la proposition</p>
                                              </div>
                                              <div>
                                                  <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Frais de dossier</label>
                                                  <div className="relative">
                                                      <input 
                                                        type="text" 
                                                        readOnly
                                                        className="w-full p-3 pr-8 border border-slate-200 rounded-xl bg-slate-100 text-slate-500 font-bold outline-none cursor-not-allowed" 
                                                        value={selectedProposal?.isSetupFeeOffered ? 'Offerts' : setupFee}
                                                      />
                                                      {!selectedProposal?.isSetupFeeOffered && (
                                                          <span className="absolute right-3 top-3.5 text-slate-400 font-bold">€</span>
                                                      )}
                                                  </div>
                                                  <p className="text-[10px] text-purple-600 font-medium mt-1">
                                                      {selectedProposal?.isSetupFeeOffered ? 'Offerts dans la proposition' : 'Fixé dans la proposition'}
                                                  </p>
                                              </div>
                                          </>
                                      ) : (
                                          <div className="md:col-span-2">
                                              <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Loyer Mensuel (€)</label>
                                              <input disabled={isLocked} type="number" className="w-full p-3 border border-slate-300 rounded-xl bg-white text-slate-900 font-bold focus:ring-2 focus:ring-amber-500 outline-none disabled:bg-slate-100 disabled:text-slate-500" value={formData.rentAmount} onChange={e => setFormData({...formData, rentAmount: Number(e.target.value)})} />
                                          </div>
                                      )}
                                  </div>

                                  <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-2 gap-6">
                                      <div>
                                          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Fait à (Ville)</label>
                                          <div className="relative">
                                               <Building className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
                                               <input disabled={isLocked} type="text" className="w-full pl-10 p-3 border border-slate-300 rounded-xl bg-white text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none disabled:bg-slate-100 disabled:text-slate-500" placeholder="Ville" value={formData.signedCity} onChange={e => setFormData({...formData, signedCity: e.target.value})} />
                                          </div>
                                      </div>
                                      <div>
                                          <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase">Date de signature</label>
                                          <div className="relative">
                                              <PenTool className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
                                              <input disabled={isLocked} type="date" className="w-full pl-10 p-3 border border-slate-300 rounded-xl bg-white text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none disabled:bg-slate-100 disabled:text-slate-500" value={formData.signedDate} onChange={e => setFormData({...formData, signedDate: e.target.value})} />
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          )}
                          
                          {selectedDealId && !isLocked && (
                              <button 
                                onClick={() => handleSave('DRAFT')} 
                                className="w-full bg-slate-900 text-white font-bold py-4 rounded-xl hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center animate-in fade-in slide-in-from-bottom-12"
                              >
                                  <FileText className="w-5 h-5 mr-3" />
                                  Générer le contrat PDF
                              </button>
                          )}
                      </div>
                  </div>
              </div>
          </div>
      );
  }

  // --- PREVIEW ---
  if (viewMode === 'preview') {
      // Use formatted date if available in formData, else today
      const signedDateDisplay = formData.signedDate 
        ? new Date(formData.signedDate).toLocaleDateString('fr-FR') 
        : new Date().toLocaleDateString('fr-FR');
        
      const startDateStr = new Date(formData.startDate || '').toLocaleDateString('fr-FR');
      
      const today = new Date().toLocaleDateString('fr-FR');
      
      return (
          <div className="p-8 h-full flex flex-col relative bg-slate-100 overflow-y-auto">
              <style>{`
                /* Automatic Page Numbering in footer */
                #contract-container-full {
                    counter-reset: page-counter;
                }
                @media print, screen {
                    .a4-page {
                        counter-increment: page-counter;
                        position: relative;
                    }
                    .a4-page::after {
                        content: counter(page-counter);
                        position: absolute;
                        bottom: 10mm;
                        right: 20mm;
                        font-size: 10px;
                        color: #94a3b8; /* slate-400 */
                    }
                }
                
                @media print {
                    .no-print { display: none !important; }
                    .print-area { display: block !important; }
                    .a4-page { 
                        page-break-after: always;
                        box-shadow: none !important;
                        margin: 0 !important;
                        width: 210mm !important;
                        height: 297mm !important;
                        overflow: hidden !important;
                    }
                }
              `}</style>

              {/* YOUSIGN MODAL - REFONTE */}
              {isYousignModalOpen && (
                  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in-95">
                          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                              <h3 className="font-bold text-xl text-slate-900 flex items-center">
                                  <div className="bg-green-100 p-2 rounded-lg mr-3">
                                      <Send className="w-5 h-5 text-green-700" />
                                  </div>
                                  Signature Électronique
                              </h3>
                              <button onClick={() => setIsYousignModalOpen(false)} className="hover:bg-slate-200 p-2 rounded-full transition-colors"><X className="w-5 h-5 text-slate-400" /></button>
                          </div>
                          
                          <div className="p-8">
                              <p className="text-sm text-slate-500 mb-6">Le contrat sera envoyé séquentiellement aux deux parties ci-dessous pour signature.</p>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                  {/* COLONNE GAUCHE: AGENCE */}
                                  <div className="space-y-4">
                                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center mb-2">
                                          <Briefcase className="w-3 h-3 mr-2" />
                                          Partie 1 : L'Agence (Vous)
                                      </h4>
                                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                                          <div>
                                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Prénom</label>
                                              <input required className="w-full p-2 text-sm border rounded bg-white" value={agencySigner.firstName} onChange={e => setAgencySigner({...agencySigner, firstName: e.target.value})} />
                                          </div>
                                          <div>
                                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Nom</label>
                                              <input required className="w-full p-2 text-sm border rounded bg-white" value={agencySigner.lastName} onChange={e => setAgencySigner({...agencySigner, lastName: e.target.value})} />
                                          </div>
                                          <div>
                                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Email</label>
                                              <input type="email" required className="w-full p-2 text-sm border rounded bg-white" value={agencySigner.email} onChange={e => setAgencySigner({...agencySigner, email: e.target.value})} />
                                          </div>
                                          <div>
                                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Téléphone (+33)</label>
                                              <input type="tel" required className="w-full p-2 text-sm border rounded bg-white" value={agencySigner.phone} onChange={e => setAgencySigner({...agencySigner, phone: e.target.value})} />
                                          </div>
                                      </div>
                                  </div>

                                  {/* COLONNE DROITE: CLIENT */}
                                  <div className="space-y-4">
                                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center mb-2">
                                          <User className="w-3 h-3 mr-2" />
                                          Partie 2 : Le Client
                                      </h4>
                                      <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 space-y-3">
                                          <div>
                                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Prénom</label>
                                              <input required className="w-full p-2 text-sm border rounded bg-white" value={clientSigner.firstName} onChange={e => setClientSigner({...clientSigner, firstName: e.target.value})} />
                                          </div>
                                          <div>
                                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Nom</label>
                                              <input required className="w-full p-2 text-sm border rounded bg-white" value={clientSigner.lastName} onChange={e => setClientSigner({...clientSigner, lastName: e.target.value})} />
                                          </div>
                                          <div>
                                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Email</label>
                                              <input type="email" required className="w-full p-2 text-sm border rounded bg-white" value={clientSigner.email} onChange={e => setClientSigner({...clientSigner, email: e.target.value})} />
                                          </div>
                                          <div>
                                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Téléphone (+33)</label>
                                              <input type="tel" required className="w-full p-2 text-sm border rounded bg-white" value={clientSigner.phone} onChange={e => setClientSigner({...clientSigner, phone: e.target.value})} />
                                          </div>
                                      </div>
                                  </div>
                              </div>

                              <div className="flex justify-end pt-8 space-x-3 border-t border-slate-100 mt-6">
                                  <button type="button" onClick={() => setIsYousignModalOpen(false)} className="px-5 py-2.5 text-slate-500 hover:bg-slate-100 rounded-lg font-medium transition-colors">Annuler</button>
                                  <button onClick={handleSendYousign} disabled={isSendingYousign} className="px-8 py-2.5 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 flex items-center shadow-lg shadow-green-900/20 transition-all disabled:opacity-70">
                                      {isSendingYousign ? <Loader className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
                                      Envoyer le contrat
                                  </button>
                              </div>
                          </div>
                      </div>
                  </div>
              )}

              {/* ACTION BAR */}
              <div className="flex justify-between items-center mb-6 no-print max-w-[210mm] mx-auto w-full">
                  {/* ... (Existing Action Bar) ... */}
                  <div className="flex gap-2">
                      <button onClick={() => setViewMode('list')} className="bg-white border border-slate-200 text-slate-700 px-4 h-10 rounded-lg font-medium flex items-center shadow-sm hover:bg-slate-50"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</button>
                      {!isLocked && (
                          <button onClick={() => setViewMode('create')} className="bg-white border border-slate-200 text-slate-700 px-4 h-10 rounded-lg font-medium flex items-center shadow-sm hover:bg-slate-50"><PenTool className="w-4 h-4 mr-2" /> Modifier</button>
                      )}
                  </div>
                  <div className="flex gap-2">
                      {formData.status !== 'SIGNED' && formData.status !== 'SENT' && (
                          <button onClick={() => handleSave('SENT')} className="bg-white border border-slate-200 text-amber-600 px-4 h-10 rounded-lg font-medium flex items-center shadow-sm hover:bg-amber-50 border-amber-100">
                              <Send className="w-4 h-4 mr-2" /> Marquer envoyé
                          </button>
                      )}
                      
                      {/* YOUSIGN BUTTON */}
                      {yousignEnabled && formData.status !== 'SIGNED' && !formData.yousignSignatureRequestId && (
                          <button 
                              onClick={handleOpenYousignModal} 
                              className="bg-green-600 text-white px-4 h-10 rounded-lg font-bold flex items-center shadow-sm hover:bg-green-700"
                          >
                              <Send className="w-4 h-4 mr-2" /> Envoyer via Yousign
                          </button>
                      )}
                      {!yousignEnabled && formData.status !== 'SIGNED' && !formData.yousignSignatureRequestId && (
                          <button 
                              disabled
                              className="bg-slate-300 text-slate-500 px-4 h-10 rounded-lg font-bold flex items-center shadow-sm cursor-not-allowed"
                              title="La signature Yousign est désactivée dans les paramètres"
                          >
                              <Send className="w-4 h-4 mr-2" /> Envoyer via Yousign
                          </button>
                      )}

                      <button onClick={generatePDF} disabled={isDownloadingPdf} className="bg-white border border-slate-200 text-slate-700 px-4 h-10 rounded-lg font-medium flex items-center shadow-sm hover:bg-slate-50">
                          {isDownloadingPdf ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                          PDF
                      </button>
                      {formData.status !== 'SIGNED' ? (
                          <button onClick={handleSignContract} className="bg-white border border-slate-200 text-slate-500 px-4 h-10 rounded-lg font-bold flex items-center shadow-sm hover:bg-slate-50 text-xs">
                              Forcer Signature
                          </button>
                      ) : (
                          <>
                            {onNavigateToManaged && selectedDealId && (
                                <button 
                                    onClick={() => {
                                        const d = deals.find(d => d.id === selectedDealId);
                                        if (d) onNavigateToManaged(d.propertyId);
                                    }} 
                                    className="bg-slate-900 text-white px-4 h-10 rounded-lg font-bold flex items-center shadow-sm hover:bg-slate-800"
                                >
                                    <Key className="w-4 h-4 mr-2" /> Voir le Bien
                                </button>
                            )}
                            <div className="bg-green-100 text-green-800 px-4 h-10 rounded-lg font-bold flex items-center border border-green-200">
                                <Check className="w-4 h-4 mr-2" /> Signé le {new Date(formData.signedAt!).toLocaleDateString()}
                            </div>
                          </>
                      )}
                  </div>
              </div>

              {/* YOUSIGN STATUS BLOCK - REDESIGNED */}
              {(formData.yousignSignatureRequestId || (formData.yousignStatus && formData.yousignStatus !== 'DRAFT')) && (
                  <div className="bg-white max-w-[210mm] mx-auto w-full mb-8 rounded-xl border border-slate-200 shadow-lg no-print overflow-hidden relative z-10 min-h-[300px]">
                      {/* HEADER DU BLOC */}
                      <div className="bg-slate-50 p-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                              <div className={`p-3 rounded-xl ${formData.yousignStatus === 'done' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  <Send className="w-6 h-6" />
                              </div>
                              <div>
                                  <h4 className="font-bold text-slate-900 text-lg">Suivi de Signature</h4>
                                  <div className="flex items-center text-xs text-slate-500 mt-1 space-x-3">
                                      <span className="font-mono bg-slate-200 px-1.5 py-0.5 rounded text-slate-600 select-all">
                                          ID: {formData.yousignSignatureRequestId}
                                      </span>
                                      <span>•</span>
                                      <span>Initié le {new Date(formData.createdAt).toLocaleDateString()}</span>
                                  </div>
                              </div>
                          </div>
                          <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${
                                  formData.yousignStatus === 'done' 
                                      ? 'bg-green-50 text-green-700 border-green-200' 
                                      : formData.yousignStatus === 'expired'
                                      ? 'bg-red-50 text-red-700 border-red-200'
                                      : 'bg-amber-50 text-amber-700 border-amber-200 animate-pulse'
                              }`}>
                                  {formData.yousignStatus === 'done' ? 'Signé' : 
                                   formData.yousignStatus === 'expired' ? 'Expiré' : 'En cours'}
                              </span>
                              <button 
                                  onClick={() => handleRefreshYousignStatus()}
                                  disabled={isRefreshingYousign}
                                  className="p-2 text-slate-500 hover:text-blue-600 hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all shadow-sm"
                                  title="Actualiser le statut"
                              >
                                  <RotateCw className={`w-5 h-5 ${isRefreshingYousign ? 'animate-spin' : ''}`} />
                              </button>
                          </div>
                      </div>
                      
                      {/* CONTENU (COLONNES) */}
                      <div className="p-6">
                          {signersStatus.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                                  {/* Barre de progression verticale visuelle */}
                                  <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-slate-100 -ml-px"></div>
                                  {[...signersStatus].sort((a: any, b: any) => {
                                      // Sort to always show agency first, then client
                                      if (a.role === 'agency') return -1;
                                      if (b.role === 'agency') return 1;
                                      return 0;
                                  }).map((signer: any, i: number) => {
                                      // Determine role from stored role property
                                      const isAgency = signer.role === 'agency';
                                      const role = isAgency ? "L'Agence (Vous)" : "Le Client";
                                      const icon = isAgency ? <Briefcase className="w-4 h-4" /> : <User className="w-4 h-4" />;
                                      const isDone = signer.status === 'signed';
                                      
                                      // Get precise date from API response
                                      // For signed: try multiple possible properties (signed_at, signedAt, signature_date, etc.)
                                      // Fallback to contract signedAt if signer date not available
                                      const rawDateSigned = signer.signed_at || signer.signedAt || signer.signature_date || signer.updated_at || signer.created_at || (isDone ? formData.signedAt : null);
                                      
                                      // For pending: use created_at or updated_at (date when invitation was sent)
                                      // Fallback to contract createdAt if signer date not available
                                      const rawDateSent = signer.created_at || signer.updated_at || formData.createdAt;
                                      
                                      // Format dates with time using toLocaleString
                                      const dateSigned = isDone && rawDateSigned 
                                          ? new Date(rawDateSigned).toLocaleString('fr-FR', { 
                                              day: '2-digit', 
                                              month: '2-digit', 
                                              year: 'numeric', 
                                              hour: '2-digit', 
                                              minute: '2-digit' 
                                          })
                                          : null;
                                      const dateSent = !isDone && rawDateSent 
                                          ? new Date(rawDateSent).toLocaleString('fr-FR', { 
                                              day: '2-digit', 
                                              month: '2-digit', 
                                              year: 'numeric', 
                                              hour: '2-digit', 
                                              minute: '2-digit' 
                                          })
                                          : null;

                                      return (
                                          <div key={signer.id} className="relative group">
                                              <div className={`p-5 rounded-xl border-2 transition-all ${
                                                  isDone 
                                                      ? 'border-green-100 bg-green-50/30' 
                                                      : 'border-amber-100 bg-amber-50/30'
                                              }`}>
                                                  <div className="flex justify-between items-start mb-3">
                                                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                                                          {icon} {role}
                                                      </div>
                                                      {isDone ? (
                                                          <div className="flex items-center text-green-600 text-xs font-bold bg-white px-2 py-1 rounded-full shadow-sm">
                                                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Signé
                                                          </div>
                                                      ) : (
                                                          <div className="flex items-center text-amber-600 text-xs font-bold bg-white px-2 py-1 rounded-full shadow-sm">
                                                              <Clock className="w-3.5 h-3.5 mr-1" /> En attente
                                                          </div>
                                                      )}
                                                  </div>

                                                  <div className="mb-4">
                                                      <p className="font-bold text-slate-900 text-base">
                                                          {signer.info?.first_name} {signer.info?.last_name}
                                                      </p>
                                                      <p className="text-sm text-slate-500 flex items-center mt-1">
                                                          <Mail className="w-3.5 h-3.5 mr-2 opacity-50" />
                                                          {signer.info?.email}
                                                      </p>
                                                      {signer.info?.phone_number && (
                                                          <p className="text-sm text-slate-500 flex items-center mt-1">
                                                              <Phone className="w-3.5 h-3.5 mr-2 opacity-50" />
                                                              {signer.info?.phone_number}
                                                          </p>
                                                      )}
                                                  </div>

                                                  <div className="pt-3 border-t border-dashed border-slate-200 flex justify-between items-center h-8">
                                                      {isDone ? (
                                                          <span className="text-[10px] text-green-600 font-medium">
                                                              Signé le {dateSigned || 'Date non disponible'}
                                                          </span>
                                                      ) : (
                                                          <div className="flex items-center justify-between w-full">
                                                              <span className="text-[10px] text-amber-600 font-medium">
                                                                  Envoyé le {dateSent || 'Date non disponible'}
                                                              </span>
                                                              <button 
                                                                  onClick={() => handleResendInvite(signer.id)}
                                                                  className="text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center transition-colors"
                                                              >
                                                                  <Mail className="w-3.5 h-3.5 mr-1" />
                                                                  Renvoyer l'email
                                                              </button>
                                                          </div>
                                                      )}
                                                  </div>
                                              </div>
                                          </div>
                                      );
                                  })}
                              </div>
                          ) : (
                              <div className="py-12 flex flex-col items-center justify-center text-center">
                                  <Loader className="w-8 h-8 text-amber-500 animate-spin mb-3" />
                                  <p className="text-slate-500 font-medium">Synchronisation des signataires...</p>
                                  <button onClick={() => handleRefreshYousignStatus()} className="mt-4 text-sm text-blue-600 hover:underline">
                                      Forcer l'actualisation
                                  </button>
                              </div>
                          )}
                      </div>
                  </div>
              )}

              {/* CONTRACT PREVIEW - A4 */}
              <div id="contract-container-full" className="flex flex-col items-center gap-8 print-area">
                  {/* ... (Contenu du contrat conservé tel quel pour économiser l'output, mais le code complet sera fourni) ... */}
                  {/* PAGE 1 */}
                  <div id="page-1" className="a4-page bg-white shadow-xl text-justify text-xs font-sans leading-relaxed text-slate-800 relative" style={{ width: '210mm', height: '297mm', padding: '20mm', overflow: 'hidden' }}>
                      {/* HEADER */}
                        <div className="text-center mb-10">
                            <p className="uppercase text-[10px] font-bold tracking-[0.2em] text-slate-400 mb-4">Strictement confidentiel</p>
                            <img src="https://kfrfvqzjxnadselwusci.supabase.co/storage/v1/object/public/app-assets/logo.png" alt="PLACID" className="h-12 mx-auto mb-6 object-contain" />
                            <h1 className="text-xl font-bold uppercase text-slate-900 mt-4 mb-2 tracking-wide leading-tight">
                                Contrat de Gestion Déléguée avec<br/>
                                Exclusivité de Propriétés Louées en<br/>
                                Courte Durée
                            </h1>
                            <p className="text-xs text-slate-400 mt-1">Réf: {formData.contractNumber}</p>
                        </div>

                        <p className="mb-4 text-slate-500 italic">Ci-après désigné par le « Contrat »,</p>
                        
                        <div className="bg-slate-50 p-6 rounded-lg border border-slate-100 mb-6">
                            <p className="mb-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Entre les soussignés</p>
                            
                            <div className="mb-6">
                                <p className="font-bold text-sm text-slate-900 mb-1">
                                    {agencyCapital > 0 ? (
                                        <>La société {agencyLegalStruct} au capital de {agencyCapital} €</>
                                    ) : (
                                        <>{agencyLegalStruct}</>
                                    )}
                                </p>
                                <div className="text-slate-600 space-y-0.5">
                                    <p>Domiciliée {agencyAddress}</p>
                                    <p>Immatriculée au RCS de {agencyRCS} : {agencySIRET}</p>
                                    <p>Représentée par {representativeName}, dûment habilité aux fins des présentes.</p>
                                </div>
                                <p className="mt-2 font-bold text-amber-600">Ci-après désigné(e) par « Le Prestataire » ou « PLACID »</p>
                            </div>

                            <div className="flex items-center my-4">
                                <div className="flex-1 h-px bg-slate-200"></div>
                                <span className="px-4 text-[10px] font-bold text-slate-400 uppercase">D'UNE PART ET</span>
                                <div className="flex-1 h-px bg-slate-200"></div>
                            </div>

                            <div>
                                <p className="font-bold text-sm text-slate-900 mb-1">{ownerDetails.civility} {selectedOwner?.firstName} {selectedOwner?.lastName}</p>
                                <div className="text-slate-600 space-y-0.5">
                                    <p>Demeurant {ownerDetails.address || selectedProperty?.address}</p>
                                    <p>Email : {selectedOwner?.email}</p>
                                    <p>Téléphone : {selectedOwner?.phone}</p>
                                </div>
                                <p className="mt-2 font-bold text-amber-600">Ci-après désigné(e) par « Le Propriétaire »</p>
                            </div>
                        </div>
                        
                        <p className="mb-8 text-center text-slate-400 text-[10px] uppercase tracking-widest">D'AUTRE PART</p>

                        <div className="mb-6 border-l-4 border-slate-900 pl-4 py-1 bg-slate-50">
                            <p className="font-bold uppercase mb-1 text-slate-900 text-xs tracking-wider">BIEN CONCERNÉ</p>
                            <p>Logement ({selectedProperty?.type}) situé <strong>{selectedProperty?.address}, {selectedProperty?.zipCode} {selectedProperty?.city}</strong>, d’une surface de <strong>{selectedProperty?.surface} m²</strong>,</p>
                            <p className="italic text-slate-500">(ci-après « le Logement »)</p>
                        </div>

                        <div className="mb-6">
                            <p className="font-bold uppercase mb-2 text-slate-900 text-xs tracking-wider">PRÉAMBULE</p>
                            <p className="mb-2">PLACID a pour activité la conciergerie immobilière de courte et moyenne durée, au moyen de ses outils technologiques et de ses prestataires.</p>
                            <p className="mb-2">C’est en raison de ces compétences que le Propriétaire a souhaité confier à PLACID le soin de gérer la location de son Logement dans les conditions indivisibles générales et particulières suivantes.</p>
                            <p>En cas de contradiction, les conditions particulières prévalent sur celles générales.</p>
                        </div>

                        <p className="font-bold uppercase text-center mt-12 mb-4 text-xs tracking-widest border-t border-b border-slate-200 py-2">CECI ÉTANT EXPOSÉ, IL A ÉTÉ CONVENU CE QUI SUIT :</p>
                        
                  </div>
                  {/* ... (Code des autres pages identiques à l'existant) ... */}
                  {/* PAGE 2 */}
                  <div id="page-2" className="a4-page bg-white shadow-xl text-justify text-xs font-sans leading-relaxed text-slate-800 relative" style={{ width: '210mm', height: '297mm', padding: '20mm', overflow: 'hidden' }}>
                        <h2 className="text-xl font-bold uppercase text-slate-900 mb-8 pb-2 border-b-2 border-amber-500 inline-block">TITRE I - CONDITIONS PARTICULIERES (« les CP »)</h2>

                        <div className="space-y-8">
                            <div>
                                <h3 className="font-bold text-sm uppercase mb-3 text-slate-800 bg-slate-100 p-2 rounded w-fit">ARTICLE 1 – OBJET DU CONTRAT</h3>
                                <p>Le présent Contrat a pour objet de fournir au Propriétaire des prestations de conciergerie :</p>
                                <div className="grid grid-cols-2 gap-2 mt-3 mb-3 pl-2">
                                    <div className="flex items-center"><div className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></div>Entretien (ménage, blanchisserie)</div>
                                    <div className="flex items-center"><div className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></div>Communication avec les locataires</div>
                                    <div className="flex items-center"><div className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></div>Accueil des locataires</div>
                                    <div className="flex items-center"><div className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></div>Petites réparations (-99€ HT)</div>
                                    <div className="flex items-center"><div className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></div>Entretien du Logement</div>
                                    <div className="flex items-center"><div className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></div>Gestion des cautions</div>
                                </div>
                                <p>Il propose en outre au Propriétaire une estimation du prix de location basée sur les données du marché.</p>
                                <p className="font-bold mt-1">Ci-après les « Prestations ».</p>
                            </div>

                            <div>
                                <h3 className="font-bold text-sm uppercase mb-3 text-slate-800 bg-slate-100 p-2 rounded w-fit">ARTICLE 2 – ENGAGEMENTS DU PRESTATAIRE</h3>
                                <p>Dans le cadre du présent Contrat, PLACID s’engage :</p>
                                <ul className="list-disc ml-5 mt-2 space-y-1 marker:text-slate-400">
                                    <li>à fournir les Prestations convenues et ceci sans interruption en assurant en cas d’empêchement pour quelque cause que ce soit, le remplacement du personnel intervenant ;</li>
                                    <li>à prendre acte des réclamations du Propriétaire et à y répondre par écrit ou oralement ;</li>
                                    <li>à garder confidentiel tout renseignement concernant le Propriétaire pendant la durée du présent Contrat et après la résiliation ou l’extinction de celui-ci pour quelque cause que ce soit ;</li>
                                    <li>à apporter à sa mission toute la diligence nécessaire en se conformant aux usages de la profession.</li>
                                </ul>
                            </div>
                        
                            <div>
                                <h3 className="font-bold text-sm uppercase mb-3 text-slate-800 bg-slate-100 p-2 rounded w-fit">ARTICLE 3 – ENGAGEMENTS DU PROPRIÉTAIRE</h3>
                                <p>De son côté, le Propriétaire s’engage :</p>
                                <div className="space-y-2 mt-2">
                                    <p>à faciliter l’exécution du présent Contrat et notamment :</p>
                                    <ul className="list-disc ml-5 mt-1 mb-2 marker:text-slate-400">
                                        <li>en mettant à disposition de PLACID et/ou ses prestataires tout ce qui est nécessaire pour accomplir ses missions,</li>
                                        <li>en respectant les conditions essentielles à la bonne exécution de celles-ci notamment le respect des règles d’hygiène et de sécurité, de la personne intervenante.</li>
                                    </ul>
                                    <p>à respecter les jours, les horaires et la durée des interventions programmées (ménage, travaux, autre…). En cas d’empêchement de nature à annuler ou reporter l’exécution de l’intervention, le Propriétaire s’engage à informer le PLACID et/ou ses prestataires au moins 48 heures à l’avance, excepté en cas de situation d’urgence (hospitalisation et accident) où le Prestataire devra être prévenu le jour-même.</p>
                                    <p>à informer PLACID par écrit, en cas d’insatisfaction concernant le déroulement de la Prestation afin de trouver une solution satisfaisante pour les deux Parties.</p>
                                    <p>à souscrire une assurance PNO ainsi qu’une assurance couvrant l’activité de meublé de tourisme.</p>
                                    <p>à déclarer son Logement auprès de la mairie et de s’acquitter des impôts liés à cette activité ou effectuer toutes formalités/déclarations nécessaires à la mise en location longue durée requises par la mairie (ex : changement de destination du bien) dont il sera seul responsable.</p>
                                    <p>à disposer des équipements nécessaires au bon fonctionnement de l’activité dans son Logement tel que listé en Annexe 1 des Conditions Particulières ou à équiper le Logement si nécessaire. Un état des lieux et un inventaire des équipements ainsi que de leur état de fonctionnement sera effectué avec le Propriétaire, et en cas de manquement aux équipements listés, PLACID, se réserve le droit de ne pas publier l'annonce du Logement.</p>
                                    <p>à définir avec PLACID le nombre maximal de Locataires qu'il peut accueillir au sein de son Logement.</p>
                                    <p>à échanger avec PLACID sur le prix de la location du Logement proposé par PLACID en cas de désaccord ou de questions.</p>
                                </div>
                            </div>
                        </div>
                        
                  </div>
                  {/* PAGE 3 */}
                  <div id="page-3" className="a4-page bg-white shadow-xl text-justify text-xs font-sans leading-relaxed text-slate-800 relative" style={{ width: '210mm', height: '297mm', padding: '20mm', overflow: 'hidden' }}>
                        <p className="mb-4">à remettre, à la date de signature du Contrat, deux (2) jeux de clés du Logement destinés à PLACID/ses prestataires, et au Locataire.</p>
                        
                        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4 text-sm">
                            <p className="font-bold text-amber-800 mb-1">Maintenance & Réparations</p>
                            <p>Le Propriétaire accepte de donner à PLACID la gestion des petites réparations courantes du bien dont le coût ne dépasserait pas la somme de <strong>99€ HT</strong>. Le Propriétaire sera informé sur la facture mensuelle de toute opération de maintenance réalisée au titre de ce service.</p>
                            <p className="mt-2">Dans l’hypothèse où une opération de maintenance nécessiterait des frais supérieurs à ceux établis, PLACID informera le Propriétaire et demandera son accord écrit préalablement à la réalisation des réparations.</p>
                        </div>

                        <div className="bg-slate-900 text-white p-4 rounded-lg mb-8">
                            <p className="font-bold uppercase tracking-wider text-xs mb-2 text-amber-500">Clause d'Exclusivité</p>
                            <p className="mb-2">Le Propriétaire :</p>
                            <ul className="list-disc ml-5 space-y-1 text-slate-300">
                                <li>accorde un contrat exclusif de prestation de service en qualité de conciergerie pour la proposition de nuitées en location courte durée meublée pour le Logement pour la durée du Contrat</li>
                                <li>accorde en conséquence l’exclusivité de l’exploitation du Logement au Prestataire</li>
                                <li>s’engage à ne jamais occuper le Logement à quelque moment de l’année que ce soit, incluant tous les jours de l'année sans exception. Une attestation pourra lui être remise sur demande à la fin de l’année civile par PLACID.</li>
                            </ul>
                        </div>

                        <h3 className="font-bold text-sm uppercase mb-4 text-slate-800 bg-slate-100 p-2 rounded w-fit">ARTICLE 4 – CONDITIONS FINANCIÈRES</h3>
                        <p className="mb-4">L’objet du présent article est d’exposer au Propriétaire des modalités de paiement dans le cadre du présent Contrat et de décrire les conditions de rémunération de PLACID dans ce cadre.</p>
                        
                        <div className="mb-6">
                            <p className="font-bold text-slate-900 mb-1">a. Modalités de paiement</p>
                            <p className="mb-1">PLACID n'encaissera ni ne reversera aucun fonds pour le compte du Propriétaire.</p>
                            <p>Les loyers versés par les Locataires seront directement versés et encaissés au Propriétaire ou à toute personne que ce dernier lui désignera.</p>
                        </div>
                        
                        <div className="mb-6 bg-slate-50 p-4 border border-slate-200 rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                                <p className="font-bold text-slate-900">b. Frais de dossier</p>
                                <span className="font-bold text-xl text-slate-900">
                                    {selectedProposal?.isSetupFeeOffered ? 'Offerts' : `${setupFee}€ HT`}
                                </span>
                            </div>
                            {selectedProposal?.isSetupFeeOffered ? (
                                <>
                                    <p className="text-xs text-slate-500 mb-2">PLACID offre au Propriétaire les frais de dossier comprenant :</p>
                                    <ul className="list-disc ml-5 text-xs space-y-1 text-slate-600">
                                        <li>Un inventaire précis du Logement comprenant des photos</li>
                                        <li>Un ménage initial pré-check-in</li>
                                        <li>La mise en place du Logement</li>
                                        <li>La création, la diffusion des annonces et la mise en place d'une politique de lancement sur les différents supports professionnels.</li>
                                        <li>Réalisation des guides d'accueil et d'arrivée</li>
                                        <li>Utilisation des logiciels métiers utilisés par PLACID prévoyant un accès privé du Propriétaire</li>
                                    </ul>
                                </>
                            ) : (
                                <>
                                    <p className="text-xs text-slate-500 mb-2">Pour la prise en charge de son dossier, le Propriétaire s'engage à régler à PLACID des frais de dossier de <strong>{setupFee}€ HT</strong> comprenant :</p>
                                    <ul className="list-disc ml-5 text-xs space-y-1 text-slate-600">
                                        <li>Un inventaire précis du Logement comprenant des photos</li>
                                        <li>Un ménage initial pré-check-in</li>
                                        <li>La mise en place du Logement</li>
                                        <li>La création, la diffusion des annonces et la mise en place d'une politique de lancement sur les différents supports professionnels.</li>
                                        <li>Réalisation des guides d'accueil et d'arrivée</li>
                                        <li>Utilisation des logiciels métiers utilisés par PLACID prévoyant un accès privé du Propriétaire</li>
                                    </ul>
                                    <p className="text-xs mt-3 italic">Ces Frais de dossiers sont facturés le mois suivant le début des locations en même temps que la première facture de commission visée ci-dessous.</p>
                                </>
                            )}
                        </div>
                        
                  </div>
                  {/* PAGE 4 */}
                  <div id="page-4" className="a4-page bg-white shadow-xl text-justify text-xs font-sans leading-relaxed text-slate-800 relative" style={{ width: '210mm', height: '297mm', padding: '20mm', overflow: 'hidden' }}>
                        
                        <div className="mb-4 bg-amber-50 p-4 border border-amber-200 rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                                <p className="font-bold text-amber-900">c. Commission</p>
                                <span className="font-bold text-xl text-amber-600">{currentCommission} % HT</span>
                            </div>
                            <p className="text-sm mb-2">Le montant de chaque location sera intégralement payé par le Locataire au Propriétaire ainsi que les frais annexes (frais dits de “ménage”).</p>
                            <p className="text-sm mb-2">Afin de rémunérer PLACID des Prestations réalisées, le Propriétaire est tenu de rémunérer PLACID mensuellement de la façon suivante :</p>
                            <ul className="list-disc ml-5 text-sm space-y-1 text-amber-900 font-medium">
                                <li>{currentCommission} % HT des revenus totaux des nuitées TTC effectivement encaissés par le Propriétaire issus des locations (hors frais de ménage),</li>
                                <li>restitution totale des frais de “ménage” réglés par les Locataires.</li>
                                <li>remboursement des frais annexes nécessaires au bon fonctionnement de la location (double de clé, achats divers pour aménagement…) selon justificatifs fournis par PLACID</li>
                                <li>restitution des frais supplémentaires payés par les Locataires pour une arrivée ou un départ en dehors des horaires fixés par PLACID et plus généralement dans le cadre d’une demande de caution.</li>
                            </ul>
                        </div>
                        
                        <p className="mb-2 text-sm">PLACID transmettra au Propriétaire une facture comprenant un compte rendu de gestion (CRG), au début du mois suivant, comprenant toutes les réservations ainsi que tous les frais. Le Propriétaire sera tenu au paiement de la facture sous un délai de 15 jours à compter de la réception de celle-ci.</p>
                        <p className="text-sm mb-4">A date anniversaire, PLACID informera le Propriétaire de l’évolution éventuelle de ses tarifs. Ces nouveaux tarifs devront faire l’objet d’un avenant au présent contrat.</p>

                        <div className="mb-4">
                            <h3 className="font-bold text-sm uppercase mb-2 text-slate-800 bg-slate-100 p-2 rounded w-fit">ARTICLE 5 - DURÉE et RÉSILIATION</h3>
                            <p>Les Parties conviennent que chacune des Parties pourra librement résilier le Contrat, en respectant un délai de préavis de <strong>{formData.duration || 'deux (2)'}</strong> mois.</p>
                            <p>La résiliation prend effet à l’issue du délai de préavis, sous réserve de l’intégrale et parfaite exécution des engagements qui ont été souscrits par chacune des Parties.</p>
                        </div>

                        <div className="mb-4">
                            <h3 className="font-bold text-sm uppercase mb-2 text-slate-800 bg-slate-100 p-2 rounded w-fit">ARTICLE 6 – LITIGES</h3>
                            <p>En cas de litige, le prestataire s’engage à rechercher un mode de résolution amiable préalablement à toute saisine juridictionnelle. Pour toute réclamation, le Propriétaire peut contacter PLACID par mail à l’adresse suivante : {agencyEmail}</p>
                        </div>

                        <div className="mb-4">
                            <h3 className="font-bold text-sm uppercase mb-2 text-slate-800 bg-slate-100 p-2 rounded w-fit">ARTICLE 7 – JURIDICTIONS COMPETENTES</h3>
                            <p>Tout différend qui pourrait naitre entre les parties quant à la validité́ l’interprétation ou l’exécution des présentes sera soumis aux juridictions compétentes.</p>
                        </div>

                        <div className="border-t-2 border-slate-900 pt-4 mt-6">
                            <div className="grid grid-cols-2 gap-8 mb-4">
                                <div>
                                    <p className="mb-1"><span className="font-bold text-slate-500 uppercase text-xs">Fait à :</span> {formData.signedCity || '__________________'}</p>
                                    <p className="mb-1"><span className="font-bold text-slate-500 uppercase text-xs">Le :</span> {signedDateDisplay}</p>
                                    <p className="text-xs italic text-slate-400">En deux (2) exemplaires originaux.</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-slate-900">Date de début des prestations :</p>
                                    <p className="text-lg">{startDateStr}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-8">
                                <div className="border-2 border-slate-200 bg-slate-50 p-6 rounded-lg h-40 relative">
                                    <p className="font-bold uppercase text-xs text-slate-500 mb-2">Pour le Prestataire (PLACID)</p>
                                    <p className="text-[10px] italic mb-8">Lu et approuvé, bon pour accord</p>
                                    {formData.status === 'SIGNED' ? 
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-green-700 font-bold text-xl border-4 border-green-700 p-2 rounded -rotate-12 opacity-80 mix-blend-multiply">SIGNÉ ÉLECTRONIQUEMENT</span>
                                        </div> 
                                        : null
                                    }
                                </div>
                                <div className="border-2 border-slate-200 bg-slate-50 p-6 rounded-lg h-40 relative">
                                    <p className="font-bold uppercase text-xs text-slate-500 mb-2">Pour le Propriétaire</p>
                                    <p className="text-[10px] italic mb-8">Lu et approuvé, bon pour accord</p>
                                    {formData.status === 'SIGNED' ? 
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-green-700 font-bold text-xl border-4 border-green-700 p-2 rounded -rotate-12 opacity-80 mix-blend-multiply">SIGNÉ ÉLECTRONIQUEMENT</span>
                                        </div> 
                                        : null
                                    }
                                </div>
                            </div>
                            
                        </div>
                  </div>
                  {/* ... (Autres pages conservées) ... */}
                  {/* PAGE 5 */}
                  <div id="page-5" className="a4-page bg-white shadow-xl text-justify text-xs font-sans leading-relaxed text-slate-800 relative" style={{ width: '210mm', height: '297mm', padding: '20mm', overflow: 'hidden' }}>
                      {/* ... contenu Annexe 1 ... */}
                      <h3 className="font-bold uppercase text-lg mb-8 text-center text-slate-900 border-b pb-4">ANNEXE 1 : ÉQUIPEMENTS OBLIGATOIRES</h3>
                      
                      <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm mb-8">
                          <p className="mb-4 font-bold text-slate-700">Le Logement doit comprendre les équipements suivants en état de fonctionnement :</p>
                          <div className="grid grid-cols-2 gap-4">
                              <div className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Réseau wifi</div>
                              <div className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Télévision</div>
                              <div className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Cafetière</div>
                              <div className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Grille-pain</div>
                              <div className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Bouilloire</div>
                              <div className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Table et fer à repasser</div>
                              <div className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Sèche-cheveux</div>
                              <div className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Aspirateur et serpillère</div>
                          </div>
                      </div>

                      <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm mb-12">
                          <p className="mb-4 font-bold text-slate-700">Le Logement doit également comprendre les équipements suivants :</p>
                          <ul className="space-y-2">
                              <li className="flex items-center"><div className="w-2 h-2 bg-slate-300 rounded-full mr-3"></div>Literie (couette et oreillers)</li>
                              <li className="flex items-center"><div className="w-2 h-2 bg-slate-300 rounded-full mr-3"></div>Cintres</li>
                              <li className="flex items-center"><div className="w-2 h-2 bg-slate-300 rounded-full mr-3"></div>Table avec un espace de bureau</li>
                              <li className="flex items-center"><div className="w-2 h-2 bg-slate-300 rounded-full mr-3"></div>Un stock de base (papier toilette, éponges, produits d'entretien, sacs poubelles)</li>
                          </ul>
                      </div>

                      <h3 className="font-bold uppercase text-lg mb-4 text-center border-t border-slate-300 pt-8">RIB et domiciliation bancaire</h3>
                      <div className="bg-slate-50 border border-slate-300 p-6 rounded-lg text-center">
                          <p className="mb-4 font-mono text-slate-400">Veuillez joindre votre RIB ou remplir les informations ci-dessous</p>
                          <div className="text-left max-w-md mx-auto space-y-4">
                              <div>
                                  <p className="text-xs uppercase font-bold text-slate-500">Banque</p>
                                  <div className="border-b border-slate-300 h-8"></div>
                              </div>
                              <div>
                                  <p className="text-xs uppercase font-bold text-slate-500">IBAN</p>
                                  <p className="font-mono text-lg tracking-widest mt-1">FR76 ____ ____ ____ ____ ____ __</p>
                              </div>
                              <div>
                                  <p className="text-xs uppercase font-bold text-slate-500">BIC</p>
                                  <div className="border-b border-slate-300 h-8 w-1/2"></div>
                              </div>
                          </div>
                      </div>
                      
                  </div>
                  {/* PAGE 6 */}
                  <div id="page-6" className="a4-page bg-white shadow-xl text-justify text-xs font-sans leading-relaxed text-slate-800 relative" style={{ width: '210mm', height: '297mm', padding: '20mm', overflow: 'hidden' }}>
                      <h2 className="text-xl font-bold uppercase text-slate-900 mb-8 border-b-2 border-slate-900 pb-2">TITRE 2 : CONDITIONS GÉNÉRALES D’UTILISATION DU SERVICE PLACID (les « CGU »)</h2>
                      {/* ... (Contenu identique) ... */}
                      <div className="grid grid-cols-2 gap-8 text-[9px] leading-tight">
                          <div>
                              <p className="font-bold mb-1 text-slate-900">1. DÉFINITIONS</p>
                              <p>Pour les besoins du présent Contrat, les termes commençant par une majuscule (et non définis par ailleurs) auront le sens qui leur est donné ci-après :</p>
                              <p>« Contrat » : Désigne le présent contrat, comprenant les Conditions Particulières et les Conditions Générales.</p>
                              <p>« Commission » : A le sens qui lui est donné dans la section 4 des CP</p>
                              <p>« Prestataire de PLACID » : Désigne le prestataire de service indépendant lié par un contrat avec PLACID réalisant les missions (i) de ménage ou de blanchisserie dans le cadre de la location saisonnière d’un Logement.</p>
                              <p>« Date d’Effet » : Désigne la date de signature du Contrat par les deux Parties.</p>
                              <p>« Informations confidentielles » : Désigne toutes les informations, quelle qu’en soit la forme (écrite, orale, informatique ou autre) qu’elles soient signalées ou non comme confidentielles dont une Partie a eu connaissance directement ou indirectement pour les besoins ou à l’occasion du Contrat.</p>
                              <p>« Locataire » : Désigne toute personne séjournant dans le Logement conformément aux termes d'un contrat de location de courte durée conclu avec le Propriétaire.</p>
                              <p>« Logement » : Désigne le bien immobilier proposé à la location saisonnière de courte durée par le Propriétaire et loué au Locataire.</p>
                              <p>« Offre de Location » : Désigne toute annonce diffusée auprès des Prospects par le Propriétaire destinée à permettre la location de son Logement.</p>
                              <p>« Partie » : Désigne individuellement et/ou collectivement PLACID et/ou le Propriétaire.</p>
                              <p>« Prestations » : Désigne l'ensemble des prestations et services réalisés par PLACID au profit du Propriétaire tel que décrites à l'Article 1 des CP et 5 des CGU.</p>
                              <p>« Propriétaire » : Désigne le propriétaire du Logement qui est désigné en tête des présentes.</p>
                              <p>« Prospect » : Désigne tout tiers intéressé par la location du Logement pour une courte période et susceptible de devenir Locataire.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-4">2. OBLIGATION DU PRESTATAIRE ET DU PROPRIÉTAIRE</p>
                              <p className="font-bold mt-2 text-slate-800">2.1. Obligations du Prestataire</p>
                              <p>PLACID est tenue à une obligation de moyens, ce que le Propriétaire reconnait. A ce titre, PLACID exécutera sa mission conformément aux règles en vigueur dans sa profession et y apportera le même soin et la même diligence que pour ses propres affaires.</p>
                              <p>PLACID sera libre de l’organisation matérielle de son travail. Toutefois, elle s’engage à répondre dans les meilleurs délais aux demandes du Propriétaire dès lors qu’elles se rapportent à l’objet du Contrat.</p>
                              <p>PLACID devra informer le Propriétaire des difficultés de toute natures rencontrés dans la réalisation des Prestations et susceptibles d’avoir des conséquences sur leur bon déroulement.</p>
                          </div>

                          <div>
                              <p className="font-bold mt-2 text-slate-800">2.2. Obligations du Propriétaire</p>
                              <p>Le Propriétaire s’engage à transmettre toute information que PLACID pourrait raisonnablement solliciter aux fins des réalisations des Prestations ainsi que l’ensemble des éléments visés à l’article 3 des Conditions Particulières.</p>
                              <p>Le Propriétaire veillera à informer PLACID dans les plus brefs délais de tout changement relatif à une information accessible sur son compte utilisateur tel que visé à l'Article 6.2 des CGU.</p>
                              <p>Il s'engage à ce que les informations communiquées à PLACID restent correctes et soient à jour en vue de la bonne réalisation des Prestations. Le Propriétaire s’engage à régler le montant de la Commission telle ci-dessus en contrepartie de la réalisation des Prestations par PLACID. Le Propriétaire s’engage à souscrire une assurance telle que visée à l’Article 10 des CGU.</p>

                              <p className="font-bold mt-2 text-slate-800">2.3. Caution</p>
                              <p>Le Propriétaire reconnait et accepte qu'une caution définie par PLACID est prise pour chaque Locataire séjournant dans le Logement du Propriétaire en fonction de la superficie du dit-Logement telles que requises par les plateformes partenaires.</p>
                              <p>En tant que prestataire de services, PLACID n'est pas responsable des dommages réalisés par les Locataires dans le Logement. PLACID s'engage cependant, si des dommages ont été constatés lors de l'état des lieux de sortie des Locataires, à gérer la relation avec la plateforme de réservation afin de récupérer tout ou partie de la caution. Le Propriétaire s'engage à fournir des justificatifs de paiement (factures) des biens endommagés sous sept (7) jours à compter de la déclaration des dommages afin de pouvoir exercer le reversement de la caution.</p>
                              <p>Une fois la caution correspondant au dommage en la possession du Propriétaire celui-ci s'engage à la reverser en tout ou partie au Prestataire selon le type de dégradation. PLACID ne sera en aucun cas responsable du refus de dédommagement par les plateformes partenaires</p>

                              <p className="font-bold mb-1 text-slate-900 mt-4">3. MONTANT DES FRAIS DE MÉNAGE, BLANCHISSERIE ET EXTRAS</p>
                              <p>Les Parties reconnaissent et acceptent que les Prestations de ménage et de blanchisserie réalisées au sein du Logement soient à la charge du Locataire. PLACID demeure libre de fixer les tarifs qu’elle souhaite s’agissant de la mise en œuvre des Prestations de ménage et de blanchisserie et encaissera la totalité des sommes perçues à ce titre, ce que le Propriétaire reconnait et accepte.</p>
                              <p>Les Parties reconnaissent que les montants des prestations de ménage et de blanchisserie peuvent être intégralement à la charge du Locataire ou partagées en deux avec le Propriétaire pour stimuler l'attractivité de l'annonce et ce uniquement à la demande du Propriétaire. Dans ce dernier cas, la moitié des frais de ménage et de blanchisserie sera alors à la charge du Locataire tandis que l'autre moitié sera à la charge du Propriétaire.</p>
                              <p>En tout état de cause, le montant des frais de ménage et de blanchisserie à la charge des Locataires figure explicitement dans l'annonce du Propriétaire comme « frais de ménage ».</p>
                              <p>Les Prestations d'accueil des voyageurs ou de départ des voyageurs s'effectuent entre 16 h ET 22h pour l’arrivée, 7h à 11h le départ. Ces mentions seront stipulées explicitement sur l'annonce du Logement.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-4">4. TAXE DE SÉJOUR</p>
                              <p>Le Propriétaire s'engage à régler les taxes de séjours redevables à la Commune sur laquelle est situé le Logement concerné par le présent Contrat, sous réserve si la plateforme de location s’occupe déjà de ce versement. Le Propriétaire s’engage à reverser ladite taxe à la Commune concernée et PLACID ne pourra être tenue responsable de l’absence de déclarations et de paiement de celle -ci. Celle-ci sera calculée en fonction du nombre de clients, le nombre de nuitées et le tarif de la taxe fixé par délibération du conseil municipal ou de l’organe délibérant de l’EPCI (Etablissement public de coopération intercommunale).</p>
                              <p>PLACID s’engage à faire parvenir au Propriétaire les différentes informations relatives à cette déclaration.</p>
                          </div>
                      </div>
                  </div>
                  {/* PAGE 7 */}
                  <div id="page-7" className="a4-page bg-white shadow-xl text-justify text-xs font-sans leading-relaxed text-slate-800 relative" style={{ width: '210mm', height: '297mm', padding: '20mm', overflow: 'hidden' }}>
                      {/* ... (Contenu identique) ... */}
                      <div className="grid grid-cols-2 gap-8 text-[9px] leading-tight">
                          <div>
                              <p className="font-bold mb-1 text-slate-900">5. MISE EN OEUVRE DES PRESTATIONS</p>
                              <p className="font-bold mt-2 text-slate-800">5.1. Accueil et départ du Locataire</p>
                              <p>Lors de l’arrivée du Locataire, PLACID se chargera de la réalisation des Prestations en lien avec l’accueil du Locataire et la préparation de son séjour.</p>
                              <p>PLACID veillera à pouvoir accueillir les voyageurs via un système automatisé et à fournir les informations nécessaires au voyageurs afin de facilité son arrivée.</p>
                              <p>PLACID pourra transmettre au Locataire toute information de nature à faciliter son séjour au sein du Logement.</p>
                              
                              <p className="font-bold mt-2 text-slate-800">5.2. Prestations de ménage et de blanchisserie</p>
                              <p>Le Propriétaire s’engage à confier exclusivement à PLACID la réalisation des Prestations de ménage et de blanchisserie relatives au Logement.</p>
                              <p>A ce titre, le Propriétaire reconnait et accepte que les Prestations de ménage et de blanchisserie soient réalisées par les Prestataires de PLACID.</p>
                              <p>Après le départ du Locataire du Logement, PLACID fera réaliser des Prestations de ménage.</p>
                              <p>Avant chaque nouvelle location, PLACID veillera à ce que le Logement soit en bon état de propreté afin de permettre à un nouveau Locataire de prendre possession du Logement pour la période de location convenue.</p>
                              <p>Conformément à l’Article 3, et sauf accord contraire entre Propriétaire et Prestataire, les frais de ménage et de blanchisserie seront à la charge du Locataire.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-4">6. COMPTE UTILISATEUR</p>
                              <p className="font-bold mt-2 text-slate-800">6.1. Création de l'Offre de Location</p>
                              <p>Le Propriétaire autorise PLACID à créer ou recréer une Offre de Location afin de permettre la mise en relation avec des Prospects et la communication entre PLACID d’une part, et le Prospect ou le Locataire d’autre part.</p>
                              
                              <p className="font-bold mt-2 text-slate-800">6.2. Création d'un compte utilisateur PLACID Propriétaire</p>
                              <p>La consultation des Prestations par le Propriétaire requiert la création d’un compte utilisateur.</p>
                              <p>Le Propriétaire pourra à tout moment consulter son compte utilisateur et accéder aux informations de son profil ainsi qu'aux dernières transactions réalisées, leurs dates et les frais annexes. PLACID communiquera au Propriétaire un identifiant et un mot de passe personnels qui lui permettront d'accéder à son compte utilisateur. Le Propriétaire demeurera seul responsable de ses identifiants qui doivent demeurer personnels et confidentiels.</p>
                              <p>Le Propriétaire avertira sans délai PLACID dans le cas où il aurait oublié ses Identifiants, ou s'il constate une utilisation détournée ou non autorisée de ses Identifiants ou la perte des informations enregistrée</p>

                              <p className="font-bold mb-1 text-slate-900 mt-4">7. EXCLUSIVITÉ</p>
                              <p>Pendant toute la durée du Contrat, le Propriétaire s'engage à recourir exclusivement aux services de PLACID pour la réalisation des Prestations visées au Contrat.</p>
                              <p>A ce titre, le Propriétaire reconnaît et accepte que seule PLACID pourra réaliser les Prestations visées au présent Contrat.</p>
                              <p>En conséquence, le Propriétaire s'interdit de procéder lui-même à la recherche de locataires via quelconque plateforme de diffusion (site internet propre, Mandataire…).</p>
                              <p>En conséquence, le Propriétaire s'engage :</p>
                              <p>-à ne pas louer personnellement le Logement, sans en aviser préalablement le Prestataire, et ce, pour la ou les périodes pour lesquelles il lui a délivré le contrat. À défaut, il sera responsable des conséquences pécuniaires du non-respect de cet engagement et répondra des éventuels dommages et intérêts réclamés par le locataire évincé ;</p>
                              <p>-à transmettre au Prestataire, toute demande de location émanant d'un locataire ayant loué antérieurement le bien par l'intermédiaire du Prestataire.</p>
                              <p>-à informer le Prestataire de la mise en vente du bien.</p>
                              <p>-à honorer les contrats de location consentis par le Prestataire en application du présent Contrat.</p>
                              <p>Toute annulation de réservation de la part du Propriétaire sera prise en compte par PLACID. Le Propriétaire se verra facturer de la commission minimum PLACID (40€TTC) sur les réservations déjà enregistrées ainsi que les frais d'annulation. Cette somme devra être réglé via un lien de paiement STRIPE.</p>
                          </div>

                          <div>
                              <p className="font-bold mb-1 text-slate-900">8. MODALITÉ D’ÉXÉCUTION</p>
                              <p>PLACID exercera et organisera ses activités librement et en toute indépendance, sans aucun lien de subordination avec le Propriétaire. Il est précisé qu’aucun mandat ou pouvoir n’est conféré à PLACID pour représenter le Propriétaire vis-à-vis de tiers. En conséquence, vis-à-vis de tiers, PLACID ne pourra pas se présenter comme représentant du Propriétaire ou comme mandataire agissant au nom et pour le compte de celui-ci.</p>
                              <p>Le Propriétaire reconnaît que le présent Contrat ne constitue pas un contrat d’agent commercial, ni un contrat de mandat de gestion locative, PLACID n’étant pas chargée de conclure des contrats, au nom et pour le compte du Propriétaire. Le Propriétaire reconnait que tout contrat pour la location du Logement sera conclu directement entre le Propriétaire et Locataire, et par l’intermédiaire d’un Mandataire, le cas échéant.</p>
                              <p>Les Prestations seront fournies par PLACID et son personnel. Néanmoins, le Propriétaire reconnait et accepte que le Prestataire puisse faire réaliser tout ou partie des Prestations au titre des présentes par un prestataire.</p>
                              <p>PLACID s'engage à gérer tout dommage ou préjudice causé par ledit Prestataire de PLACID, notamment sans que cela ne soit limitatif en cas d'enlèvement inopiné d'objets ou de vol.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-4">9. RESPONSABILITE</p>
                              <p>PLACID a vocation à fournir des Prestations de service et de conseils destinés au Propriétaire dans le cadre de l'Offre de Location de son Logement en vue de sa location pour des courtes périodes soit par le Propriétaire directement auprès des Prospects et des Locataires soit par le biais des Prestataires de PLACID.</p>
                              <p>Le présent Contrat ne constitue pas un contrat de mandat de gestion locative, PLACID n’étant pas chargé de conclure des contrats, au nom et pour le compte du Propriétaire, ce que le Locataire reconnaît et accepte.</p>
                              <p>A ce titre, PLACID ne garantit en aucun cas que le Logement sera constamment occupé ou qu'un certaintaux d'occupation sera atteint. La responsabilité de PLACID ne saurait en aucun cas être recherchée en cas d'absence de réservation ou de taux d'occupation faible.</p>
                              <p>Chaque Partie est responsable vis-à-vis de l’autre de tout dommage direct qu’elle lui causerait, par sa faute, dans le cadre de l’exécution du Contrat.</p>
                              <p>La responsabilité de PLACID ne saurait être engagée en cas de force majeure. PLACID ne peut en aucun cas engager sa responsabilité pour tout dommage spécial, indirect, ou conséquent incluant sans limitation la perte de vente ou d’activité, la perte de profit ou de clientèle encourue ou supportée par le Propriétaire.</p>
                              <p>Sauf préjudice corporel, négligence, faute intentionnelle ou grave, la responsabilité totale de PLACID en réparation de l’ensemble des préjudices subis par le Propriétaire au titre de l’exécution des Prestations objets du Contrat ne pourra en aucun cas dépasser le montant total des Commissions reçues par PLACID pendant toute la durée du Contrat.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-4">10. ASSURANCE</p>
                              <p>Le Propriétaire déclare avoir souscrit auprès d’une compagnie notoirement solvable, une police d’assurance PNO et couvrant l’activité de location longue durée garantissant les conséquences de la responsabilité qu’il est susceptible d’encourir dans le cadre de l’exécution du Contrat.</p>
                              <p>En tout état de cause, le Propriétaire veillera à ce que sa couverture soit toujours suffisante au regard des risques inhérents ou consécutifs à l'exercice d'une activité de location de courte durée relative à son Logement.</p>
                              <p>A la date de signature du Contrat et ultérieurement sur demande de PLACID, le Propriétaire s’engage à fournir à PLACID une attestation de la souscription de cette assurance.</p>
                              <p className="font-bold mb-1 text-slate-900 mt-4">11. RÉGLEMENTATION</p>
                              <p>Le Propriétaire s'engage à se conformer à la réglementation applicable (nationale ou locale) s'agissant de la location de son Logement, notamment en matière de location de courte durée. A cet égard, le Propriétaire déclare :</p>
                              <p>i. Être parfaitement informé de la réglementation applicable notamment en matière de location de courte durée ;</p>
                          </div>
                      </div>
                      
                  </div>
                  {/* PAGE 8 */}
                  <div id="page-8" className="a4-page bg-white shadow-xl text-justify text-xs font-sans leading-relaxed text-slate-800 relative" style={{ width: '210mm', height: '297mm', padding: '20mm', overflow: 'hidden' }}>
                      {/* ... (Contenu identique) ... */}
                      <div className="grid grid-cols-2 gap-8 text-[9px] leading-tight">
                          <div>
                              <p>Le Propriétaire s'engage à se conformer à la réglementation applicable (nationale ou locale) s'agissant de la location de son Logement, notamment en matière de location de courte durée. A cet égard, le Propriétaire déclare :</p>
                              <p>i. Être parfaitement informé de la réglementation applicable notamment en matière de location de courte durée ;</p>
                              <p>ii. Respecter les prescriptions et la règlementation fiscale applicable ;</p>
                              <p>iii. S’être acquitté des déclarations requises auprès des autorités compétentes, le cas échéant ;</p>
                              <p>iv. Disposer de tous les droits et autorisations nécessaires pour proposer son Logement à la location. Notamment, en cas de propriété indivise, le Propriétaire devra obtenir et justifier de l'accord des autres indivisaires.</p>
                              <p>v. Disposer d'un numéro d'enregistrement obligatoire pour garantir la Commercialisation de l'Offre de Location sur les plateformes mandataires.</p>
                              <p>vi. Vérifier auprès de la Copropriété que l’activité de location courte durée est autorisée</p>
                              <p>Le Propriétaire fera son affaire de toute autorisation et déclaration nécessaires à l'exercice d'une activité et veillera à respecter toute nouvelle réglementation le concernant.</p>
                              <p>Le Propriétaire s'engage à justifier du respect de la législation applicable et notamment de la réalisation des déclarations auprès des autorités compétentes, le cas échéant, sur simple demande de PLACID. PLACID n'a aucune obligation de conseil à cet égard et ne pourra en aucun cas voir sa responsabilité engagée dans le cas où le Propriétaire ne respecterait pas les prescriptions légales applicables. Conformément à l'Article 242 bis du Code Général des Impôts, PLACID pourra transmettre sur demande aux Propriétaires un relevé des revenus bruts générés issu des Prestations. Ce relevé est basé sur les revenus perçus par les Propriétaires l'année précédente (N-1).</p>

                              <p className="font-bold mb-1 text-slate-900 mt-4">12. RÉSILIATION</p>
                              <p>Chacune des Parties pourra résilier le Contrat à tout moment sous réserve de respecter un préavis écrit de quatre-vingt-dix (90) jours adressés par courrier avec accusé de réception.</p>
                              <p>En cas de manquement par l'une des Parties à l'une de ses obligations au titre du Contrat, non réparé dans un quinze (15) jours à compter de l'envoi d'une mise en demeure adressée par l'autre Partie par lettre recommandée avec accusé de réception, le Contrat pourra être résilié de plein droit sans formalité judiciaire par l'autre Partie, sans préjudice des éventuels dommages et intérêts auxquels elle pourrait prétendre du fait de ce manquement.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-4">13. CONSEQUENCES DE LA REALISATION ET DES TERMES DU CONTRAT</p>
                              <p>En cas de résiliation du Contrat ou au terme de celui-ci, PLACID restituera au Propriétaire les deux (2) jeux de clés du Logement. En cas de résiliation du contrat dans les quarante-cinq (45) jours suivant la première nuitée facturée, le Propriétaire s'engage à payer à PLACID, la somme forfaitaire prévue aux CP correspondant aux frais de mise en place de l'Offre du Logement.</p>
                              <p>PLACID veillera à ce que le Logement soit restitué en bon état de propreté.</p>
                              <p>Le terme du Contrat ou sa résiliation entraine la clôture du compte utilisateur PLACID ainsi que la suppression des données y figurant, ce que le Propriétaire reconnaît.</p>
                              <p className="font-bold mb-1 text-slate-900 mt-4">14. CONFIDENTIALITÉ</p>
                              <p>Chaque Partie s’engage, tout au long de la durée du Contrat à ne divulguer à quiconque aucune Information Confidentielle de l’autre Partie.</p>
                              <p>Chaque Partie peut divulguer des Informations Confidentielles de l’autre Partie :</p>
                              <p>I. À ceux de ses employés, cadres, représentants et conseils qui ont besoin de connaitre une telle information afin de permettre à la Partie concernée de remplir ses obligations au titre du Contrat.</p>
                              <p>Chaque Partie s’assurera que ses employés, cadres, représentants et conseils auxquels elle divulgue les Informations Confidentielles de l’autre Partie respectent les dispositions du présent Article ; et</p>
                              <p>II. Si cela est requis par la loi, par un tribunal ou par une autorité gouvernementale ou règlementaire compétente, sous réserve qu’une notification relative à une telle divulgation requise ou rendue nécessaire soit fournie à l’autre Partie dès que possible avant la divulgation.</p>
                            </div>

                          <div>
                              <p>Aucune des Parties ne devra utiliser des Informations Confidentielles de l’autre Partie pour d’autres motifs que l’exécution de ses obligations au titre des présentes.</p>
                              <p>Excepté lorsque cela est rendu nécessaire au titre d’une notification gouvernementale ou afin de se conformer avec les lois et règlements applicables ou afin de faire valoir leurs droits respectifs au titre du présent Contrat, et excepté toute convention écrite contraire des Parties, celles-ci devront :</p>
                              <p>i. Conserver confidentielles les dispositions matérielles du présent Contrat ;</p>
                              <p>ii. Raisonnablement s’accorder sur les termes et le moment exact de toute annonce publique relative aux transactions envisagées au sein des présentes.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-3">15. INFORMATIQUE ET LIBERTÉS – DONNÉES PERSONNELLES</p>
                              <p>PLACID s’engage à respecter toutes les obligations résultant de l’application de toute législation applicable relative à la protection des données à caractère personnel et de la vie privée, y compris celles transposant la Directive européenne CE/95/46, pour toutes les données personnelles traitées par elle dans le cadre de l’exécution de ses obligations au titre du présent Contrat.</p>
                              <p>Les données personnelles communiquées, collectées ou produites à l’occasion de l’exécution des Prestations ne pourront faire l’objet d’aucune opération ou intégration dans un fichier et ce, quelle que soit la nature de l’opération ou le procédé utilisé, autres que celles prévues au présent Contrat.</p>
                              <p>PLACID s’engage notamment à respecter les obligations suivantes et à les faire respecter par son personnel, et ses éventuels sous-traitants, le cas échéant :</p>
                              <p>- Ne pas opérer ou intégrer les données personnelles dans un fichier et ce, quelle que soit la nature de l’opération ou le procédé utilisé, sauf cas expressément prévus au présent Contrat ;</p>
                              <p>- N’intervenir que sur instruction du Propriétaire et en conséquence ne traiter les données personnelles que conformément aux instructions écrites du Propriétaire ;</p>
                              <p>- Ne pas divulguer les données personnelles à des tiers en dehors des cas expressément prévus dans le Contrat, ou en l’absence d’autorisation écrite du Propriétaire, ou en dehors des cas prévus par une disposition légale ou réglementaire ;</p>
                              <p>- Ne rendre accessibles et consultables les données personnelles qu’aux seuls personnels dûment habilités et autorisés en raison de leurs fonctions et qualités, dans la stricte limite de ce qui leur est nécessaire à l’accomplissement de leurs fonctions ;</p>
                              <p>- Adopter toutes les mesures nécessaires pour garantir la sécurité, l’intégrité, la confidentialité et le contrôle de la divulgation des données à caractère personnel ;</p>
                              <p>- Recourir exclusivement à des moyens de traitement de données à caractère personnel situés sur le territoire d’un pays membre de l’Espace Économique Européen.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-3">16. FORCE MAJEURE</p>
                              <p>Pour les besoins de cet Article, « Evènement de Force Majeure » signifie un évènement dont la réalisation est au-delà de tout contrôle raisonnable par l’une ou l’autre des Parties, incluant de manière non exhaustive les éléments suivants : catastrophes naturelles, guerres, explosions, incendies, inondations, tempêtes, épidemies et pandémies, tremblements de terre, insurrections, actes terroristes, émeutes, troubles civils, rébellions, et lock-out ou conflit sociaux impliquant la Partie demandant le bénéfice de cet Article.</p>
                              <p>Si un Evènement de Force Majeure rendant impossible l’exécution de ses obligations par une Partie (la « Partie Affectée ») à l’égard de l’autre Partie (l’« Autre Partie ») survient, la Partie Affectée ne sera pas responsable à l’égard de l’Autre Partie et devra être déchargée de ses obligations dans la mesure où sa capacité de remplir ses obligations a été affectée par un Evènement de Force Majeure.</p>
                              <p>Si les conséquences de l’Evènement de Force Majeure sur la Partie Affectée rend impossible pour cette partie l’exécution d’une part substantielle de ses obligations au titre du Contrat pendant une période d’au moins quinze (15) jours consécutifs, l’Autre Partie pourra, par notification écrite, résilier le présent Contrat soit pour partie ou en totalité (relativement à toute ou partie des Prestations ayant été affectés par l’Evènement de Force Majeure), avec effet immédiat et sans responsabilité à l’égard de la Partie Affectée.</p>
                          </div>
                      </div>
                  </div>
                  {/* PAGE 9 */}
                  <div id="page-9" className="a4-page bg-white shadow-xl text-justify text-xs font-sans leading-relaxed text-slate-800 relative" style={{ width: '210mm', height: '297mm', padding: '20mm', overflow: 'hidden' }}>
                      {/* ... (Contenu identique) ... */}
                      <div className="grid grid-cols-2 gap-5 text-[9px] leading-tight">
                          <div>
                              <p className="font-bold mb-1 text-slate-900">17. CESSION</p>
                              <p>PLACID pourra céder librement tout ou partie des droits découlant du présent Contrat notamment dans l'hypothèse d'une restructuration de PLACID, que ce soit dans le cadre d'une acquisition, fusion/absorption, apport partiel d'actifs ou cession de fonds de commerce. Dans ce cas, le cessionnaire sera tenu aux mêmes droits et obligations que PLACID. En cas de cession, PLACID s’engage à en informer dans les plus bref délai le Propriétaire.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-3">18. INDÉPENDANCE DES PARTIES</p>
                              <p>Aucune disposition du présent Contrat n’a pour objet et ne devra être réputée établir une société de fait ou créée de fait ou une joint-venture entre les Parties, ni constituer un contrat de mandat ou de travail vis-à-vis de l’autre Partie, ni autoriser une Partie à s’engager ou prendre des engagements au nom de l’autre.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-3">19. MODIFICATION DU CONTRAT</p>
                              <p>Les modifications, amendements et additions faites aux présentes ne seront valides que si faites par écrit et signées par les deux Parties.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-3">20. ABSENCE DE RENONCIATION</p>
                              <p>L’absence ou le retard dans l’exercice par l’une des Parties d’un de ses droits ou recours au titre des présentes ou du droit applicable ne constitue pas une renonciation au droit ou recours considéré, pas plus qu’il ne pourra empêcher ou restreindre l’exercice ultérieur de celui-ci ou de tout autre droit ou recours.</p>
                              <p>L’exercice unique ou partiel d’un tel droit ou recours ne pourra faire obstacle ou limiter l’exercice ultérieur de ce droit ou recours.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-3">21. INFORMATION PRECONTACTUELLE</p>
                              <p>Le Propriétaire reconnaît avoir été informé, préalablement à la conclusion du Contrat, d’une manière lisible et compréhensible, au moyen de la mise à disposition des présentes CGV et conformément aux dispositions de l’article L. 221-5 du Code de la consommation :</p>
                              <p>-sur les caractéristiques essentielles des Prestations;</p>
                              <p>-sur le prix des Prestations ;</p>
                              <p>-sur les modalités de paiement, de fourniture et d’exécution du Contrat;</p>
                              <p>-en l’absence d’exécution immédiate du Contrat, sur la date ou le délai auquel PLACID s’engage à fournir les Prestations commandées ;</p>
                              <p>-sur l’identité du Prestataire, ses coordonnées postales, téléphoniques et électroniques, ainsi que sur ses activités;</p>
                              <p>-sur les garanties légales et contractuelles et, le cas échéant, leurs modalités de mise en œuvre ;</p>
                              <p>-sur la possibilité de recourir à une médiation conventionnelle en cas de litige ;</p>
                              <p>-sur le droit de rétractation (existence, conditions, délai, modalités d’exercice de ce droit et formulaire type de rétractation), les modalités de résiliation et autres conditions contractuelles importantes.</p>
                          </div>

                          <div>
                              <p className="font-bold mb-1 text-slate-900">22. RETRACTATION EN CAS DE VENTE HORS ETABLISSEMENT</p>
                              <p>Suivant l'article L121-18 du Code de la consommation, le Propriétaire dispose de 14 jours à partir de la conclusion du contrat pour résilier le Contrat.</p>
                              <p>Pour cela, conformément à l’article L221-20 du code de la consommation, il convient d’informer le Prestataire de sa décision de se rétracter par l'envoi, avant l'expiration du délai prévu à l'article L. 221-18, du formulaire de rétractation mentionné au 7° de l'article L. 221-5 ou de toute autre déclaration, dénuée d'ambiguïté, exprimant sa volonté de se rétracter. Le Prestataire ne pourra prendre en compte aucune demande de rétractation orale.</p>
                              <p>Il ne sera redevable d'aucune somme au titre de pénalités ou autres. Tout acompte versé à la commande lui sera remboursé intégralement, au plus tard dans les 14 jours après la notification de la rétractation (suivant l'article L221-24 du Code de la consommation). La Prestation ne prendra effet qu'à l'échéance de ce délai de 14 jours si le Propriétaire n'a pas utilisé son droit pour se rétracter (suivant l'article L221-18 du Code de la consommation).</p>
                              <p>Les Prestations commandées prendront effet au terme de la période de rétractation. Toutefois s'il est certain qu'il ne souhaite pas utiliser son droit de rétractation et qu'il veut que les Prestations commencent avant ce délai de 14 jours, le Propriétaire peut renoncer expressément à son droit de rétractation en l'indiquant de manière manuscrite et expresse (suivant l'article L221-25 du Code de la consommation). Le Prestataire devra demander au Propriétaire de reconnaître qu'après qu'il aura entièrement exécuté le contrat, celui-ci ne disposera plus du droit de rétractation. Dès lors, les Prestations pourront commencer à réception du contrat signé.</p>
                              <p>Conformément à l’alinéa 2 de l’article L221-25 du Code de la consommation, si le Propriétaire a exercé son droit de rétractation alors que l'exécution du Contrat a commencé, à sa demande expresse, avant la fin du délai de rétractation, il versera au Prestataire un montant correspondant au service fourni jusqu'à la communication de sa décision de se rétracter ; ce montant est proportionné au prix total de la prestation convenu dans le contrat. Si le prix total est excessif, le montant approprié est calculé sur la base de la valeur marchande de ce qui a été fourni. Cela pourra par exemple correspondre aux frais de dossier.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-3">23. RECLAMATIONS</p>
                              <p>En cas de litige relatif aux présentes Conditions Générales, le Propriétaire à la faculté de recourir gratuitement à un médiateur de la consommation conformément aux articles L612-1 du Code de la consommation. Une liste des médiateurs agréés est disponible sur le site internet de la médiation de la consommation accessible à l’adresse suivante : https://www.economie.gouv.fr/mediation-conso/mediateurs-références#secteur%2014</p>
                              <p>Le Propriétaire a en outre la possibilité de déposer une plainte via la plateforme de règlement en ligne des litiges (dites plateforme « RLL ») accessible au moyen du lien ci-dessous : https://ec.europa.eu/consumers/odr/main/? event=main.complaints.screeningphase. En cas d’échec dans la désignation du médiateur ou de la médiation elle-même, la partie la plus diligente peut saisir le tribunal compétent.</p>

                              <p className="font-bold mb-1 text-slate-900 mt-3">24. ENTREE EN VIGUEUR</p>
                              <p>Les présentes conditions générales sont entrées en vigueur le {today}.</p>
                          </div>
                      </div>
                      
                  </div>

              </div>
          </div>
      );
  }

  return <div>Chargement...</div>;
};